import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = 'https://civicbot-api-h6ffhbgbbeatagf6.westus2-01.azurewebsites.net';

// ─── Category Colors ───────────────────────────────────────────────────────────
const CATEGORY_CONFIG = {
  'UTILITIES':    { color: '#0EA5E9', bg: '#0EA5E910', label: 'Utilities' },
  'PUBLIC WORKS': { color: '#10B981', bg: '#10B98110', label: 'Public Works' },
  'PARKS':        { color: '#8B5CF6', bg: '#8B5CF610', label: 'Parks' },
  'PLANDEV':      { color: '#F59E0B', bg: '#F59E0B10', label: 'Planning & Dev' },
  'DEFAULT':      { color: '#6B7280', bg: '#6B728010', label: 'General' },
};

function getCat(code) {
  return CATEGORY_CONFIG[code] || CATEGORY_CONFIG['DEFAULT'];
}

// ─── API helper with JWT ───────────────────────────────────────────────────────
function apiFetch(url, options = {}) {
  const token = localStorage.getItem('civicbot_token');
  return fetch(`${API_BASE}${url}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ─── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; background: #0A0F1C; color: #E2E8F0; min-height: 100vh; }
  :root {
    --accent: #3B82F6; --accent-light: #60A5FA; --accent-dim: #3B82F620;
    --surface: #111827; --surface2: #1A2235; --surface3: #222D42;
    --border: #1E2D45; --border-light: #2A3F5F;
    --text: #E2E8F0; --text-muted: #64748B; --text-dim: #94A3B8;
    --success: #10B981; --warning: #F59E0B; --danger: #EF4444;
  }
  .app { display: flex; flex-direction: column; min-height: 100vh; }

  /* ── Login ── */
  .login-wrap { display: flex; align-items: center; justify-content: center; min-height: 100vh;
    background: radial-gradient(ellipse at 30% 20%, #1E3A5F30 0%, transparent 60%),
                radial-gradient(ellipse at 70% 80%, #0EA5E915 0%, transparent 60%), #0A0F1C; }
  .login-card { width: 420px; background: var(--surface); border: 1px solid var(--border); border-radius: 16px; padding: 48px; box-shadow: 0 24px 80px #00000060; }
  .login-logo { display: flex; align-items: center; gap: 12px; margin-bottom: 36px; }
  .login-logo-icon { width: 42px; height: 42px; border-radius: 10px; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 20px; }
  .login-logo-text { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .login-logo-text span { color: var(--accent-light); }
  .login-subtitle { color: var(--text-muted); font-size: 14px; margin-bottom: 32px; line-height: 1.5; }
  .login-field { margin-bottom: 16px; }
  .login-label { display: block; font-size: 12px; font-weight: 500; color: var(--text-dim); margin-bottom: 8px; letter-spacing: 0.5px; text-transform: uppercase; }
  .login-input { width: 100%; padding: 12px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 14px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .login-input:focus { border-color: var(--accent); }
  .login-btn { width: 100%; padding: 13px; background: var(--accent); border: none; border-radius: 8px; color: white; font-size: 14px; font-weight: 600; font-family: inherit; cursor: pointer; margin-top: 8px; transition: background 0.2s, transform 0.1s; }
  .login-btn:hover { background: #2563EB; }
  .login-btn:active { transform: scale(0.99); }
  .login-btn:disabled { background: var(--surface3); color: var(--text-muted); cursor: not-allowed; }
  .login-error { color: var(--danger); font-size: 13px; margin-top: 12px; text-align: center; }
  .login-forgot { text-align: center; margin-top: 14px; font-size: 12px; color: var(--text-muted); }
  .login-forgot a { color: var(--accent-light); text-decoration: none; cursor: pointer; }
  .login-forgot a:hover { text-decoration: underline; }

  /* ── Layout ── */
  .header { height: 60px; display: flex; align-items: center; justify-content: space-between; padding: 0 28px; background: var(--surface); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 10; }
  .header-logo { display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 600; }
  .header-logo-icon { width: 30px; height: 30px; border-radius: 7px; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 14px; }
  .header-right { display: flex; align-items: center; gap: 20px; }
  .header-status { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--success); box-shadow: 0 0 6px var(--success); }
  .logout-btn { padding: 7px 14px; background: transparent; border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-muted); font-size: 12px; font-family: inherit; cursor: pointer; transition: all 0.2s; }
  .logout-btn:hover { border-color: var(--accent); color: var(--text); }
  .main { display: flex; flex: 1; }
  .sidebar { width: 220px; background: var(--surface); border-right: 1px solid var(--border); padding: 20px 12px; display: flex; flex-direction: column; gap: 4px; }
  .sidebar-label { font-size: 10px; font-weight: 600; color: var(--text-muted); letter-spacing: 1px; text-transform: uppercase; padding: 4px 10px; margin-top: 8px; }
  .sidebar-item { display: flex; align-items: center; gap: 10px; padding: 9px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500; color: var(--text-muted); transition: all 0.15s; border: 1px solid transparent; }
  .sidebar-item:hover { background: var(--surface2); color: var(--text); }
  .sidebar-item.active { background: var(--accent-dim); color: var(--accent-light); border-color: var(--accent)30; }
  .content { flex: 1; padding: 32px; overflow-y: auto; }

  /* ── Page header ── */
  .page-header { margin-bottom: 28px; }
  .page-title { font-size: 22px; font-weight: 600; letter-spacing: -0.5px; }
  .page-subtitle { font-size: 13px; color: var(--text-muted); margin-top: 4px; }

  /* ── Stats row ── */
  .stats-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 28px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 20px 24px; }
  .stat-label { font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; }
  .stat-value { font-size: 28px; font-weight: 600; margin-top: 6px; letter-spacing: -1px; }
  .stat-sub { font-size: 12px; color: var(--text-muted); margin-top: 4px; }

  /* ── Card ── */
  .card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .card-header { padding: 18px 24px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .card-title { font-size: 14px; font-weight: 600; }
  .card-body { padding: 24px; }

  /* ── SR Template list ── */
  .filter-row { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
  .search-input { flex: 1; padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .search-input:focus { border-color: var(--accent); }
  .filter-btn { padding: 9px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text-muted); font-size: 12px; font-family: inherit; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .filter-btn:hover, .filter-btn.active { border-color: var(--accent); color: var(--accent-light); }
  .select-all-row { display: flex; align-items: center; justify-content: space-between; padding: 10px 16px; background: var(--surface2); border-radius: 8px; margin-bottom: 12px; font-size: 12px; color: var(--text-muted); }
  .select-all-btn { background: none; border: none; color: var(--accent-light); font-size: 12px; font-family: inherit; cursor: pointer; font-weight: 500; }
  .select-all-btn:hover { text-decoration: underline; }
  .sr-list { display: flex; flex-direction: column; gap: 6px; }
  .sr-item { display: flex; align-items: center; gap: 14px; padding: 12px 16px; background: var(--surface2); border: 1px solid var(--border); border-radius: 8px; cursor: pointer; transition: all 0.15s; }
  .sr-item:hover { border-color: var(--border-light); background: var(--surface3); }
  .sr-item.selected { border-color: var(--accent)50; background: var(--accent-dim); }
  .sr-checkbox { width: 18px; height: 18px; border-radius: 4px; border: 2px solid var(--border-light); display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.15s; }
  .sr-item.selected .sr-checkbox { background: var(--accent); border-color: var(--accent); }
  .sr-checkbox-check { color: white; font-size: 11px; font-weight: 700; }
  .sr-info { flex: 1; min-width: 0; }
  .sr-name { font-size: 13px; font-weight: 500; }
  .sr-code { font-size: 11px; color: var(--text-muted); font-family: 'DM Mono', monospace; margin-top: 2px; }
  .sr-badge { font-size: 10px; font-weight: 600; padding: 3px 8px; border-radius: 4px; letter-spacing: 0.3px; flex-shrink: 0; }
  .sr-public-badge { font-size: 10px; font-weight: 500; padding: 2px 7px; border-radius: 4px; background: #10B98115; color: #10B981; flex-shrink: 0; }

  /* ── Embed code ── */
  .embed-box { background: #080D18; border: 1px solid var(--border); border-radius: 8px; padding: 16px 20px; position: relative; }
  .embed-code { font-family: 'DM Mono', monospace; font-size: 12px; color: #7DD3FC; white-space: pre-wrap; word-break: break-all; line-height: 1.6; }
  .copy-btn { position: absolute; top: 12px; right: 12px; padding: 6px 12px; background: var(--surface3); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-dim); font-size: 11px; font-family: inherit; cursor: pointer; transition: all 0.15s; }
  .copy-btn:hover { border-color: var(--accent); color: var(--accent-light); }
  .copy-btn.copied { background: #10B98120; border-color: #10B981; color: #10B981; }

  /* ── Save bar ── */
  .save-bar { position: fixed; bottom: 28px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid var(--border-light); border-radius: 12px; padding: 14px 20px; display: flex; align-items: center; gap: 16px; box-shadow: 0 8px 40px #00000080; animation: slideUp 0.3s ease; }
  @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(20px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
  .save-bar-text { font-size: 13px; color: var(--text-muted); }
  .save-bar-text strong { color: var(--text); }
  .save-btn { padding: 9px 20px; background: var(--accent); border: none; border-radius: 8px; color: white; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; transition: background 0.2s; }
  .save-btn:hover { background: #2563EB; }
  .save-btn.saved { background: var(--success); }

  /* ── Settings ── */
  .settings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .setting-field { display: flex; flex-direction: column; gap: 6px; }
  .setting-label { font-size: 12px; font-weight: 500; color: var(--text-dim); }
  .setting-input { padding: 10px 14px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; color: var(--text); font-size: 13px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .setting-input:focus { border-color: var(--accent); }
  .setting-full { grid-column: 1 / -1; }

  /* ── Toggle switch ── */
  .toggle-row { display: flex; align-items: center; justify-content: space-between; padding: 14px 16px; background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; }
  .toggle-row-info { flex: 1; }
  .toggle-row-label { font-size: 13px; font-weight: 600; color: var(--text); }
  .toggle-row-sub { font-size: 12px; color: var(--text-muted); margin-top: 3px; line-height: 1.4; }
  .toggle-switch { width: 44px; height: 24px; border-radius: 12px; border: none; cursor: pointer; position: relative; transition: background 0.2s; flex-shrink: 0; margin-left: 16px; background: var(--border-light); }
  .toggle-switch.on { background: var(--accent); }
  .toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: white; transition: transform 0.2s; box-shadow: 0 1px 4px #00000040; }
  .toggle-switch.on .toggle-thumb { transform: translateX(20px); }

  /* ── Submissions table ── */
  .submissions-table { width: 100%; border-collapse: collapse; }
  .submissions-table th { padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--border); }
  .submissions-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .submissions-table tr:hover td { background: var(--surface2); }
  .delivery-badge { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .delivery-badge.email { background: #F59E0B15; color: #F59E0B; border: 1px solid #F59E0B30; }
  .delivery-badge.cityworks { background: #3B82F615; color: #60A5FA; border: 1px solid #3B82F630; }
  .completed-row td { background: #10B98108; }
  .sub-check { width: 16px; height: 16px; cursor: pointer; accent-color: var(--accent); }
  .notes-input { width: 100%; padding: 4px 8px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text); font-size: 11px; font-family: inherit; outline: none; transition: border-color 0.2s; }
  .notes-input:focus { border-color: var(--accent); background: var(--surface3); }
  @media print {
    .header, .sidebar, .save-bar, .card-header button, .filter-btn, .search-input, .notes-input, .sub-check { display: none !important; }
    body { background: white !important; color: black !important; }
    .card { border: 1px solid #ccc !important; box-shadow: none !important; }
    .submissions-table th, .submissions-table td { color: black !important; border-color: #ccc !important; font-size: 11px !important; padding: 6px 8px !important; }
    .content { padding: 0 !important; }
    .stat-card { background: white !important; border: 1px solid #ccc !important; }
    .stat-value, .stat-label, .stat-sub, .page-title, .page-subtitle { color: black !important; }
  }

  /* ── Billing card ── */
  .billing-card { background: linear-gradient(135deg, #1E3A5F, #1a2235); border: 1px solid #2A3F5F; border-radius: 12px; padding: 24px; margin-bottom: 20px; }
  .billing-trial-badge { display: inline-block; background: #10B98120; border: 1px solid #10B98140; color: #10B981; font-size: 11px; font-weight: 600; padding: 4px 10px; border-radius: 20px; margin-bottom: 12px; }
  .billing-trial-badge.warning { background: #F59E0B20; border-color: #F59E0B40; color: #F59E0B; }
  .billing-trial-badge.danger { background: #EF444420; border-color: #EF444440; color: #EF4444; }
  .billing-plan-name { font-size: 18px; font-weight: 600; color: var(--text); margin-bottom: 4px; }
  .billing-trial-days { font-size: 13px; color: var(--text-muted); margin-bottom: 16px; }
  .billing-plans-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 16px; }
  .billing-plan-option { background: var(--surface2); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px; cursor: pointer; transition: all 0.15s; text-align: center; }
  .billing-plan-option:hover { border-color: var(--accent); background: var(--accent-dim); }
  .billing-plan-option.current { border-color: var(--accent); background: var(--accent-dim); }
  .billing-plan-price { font-size: 18px; font-weight: 700; color: var(--text); }
  .billing-plan-name-sm { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .billing-plan-subs { font-size: 10px; color: var(--text-muted); margin-top: 2px; }

  /* ── Loading / Empty ── */
  .loading { display: flex; align-items: center; justify-content: center; padding: 60px; gap: 12px; color: var(--text-muted); font-size: 14px; }
  .spinner { width: 20px; height: 20px; border: 2px solid var(--border-light); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.8s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  .empty { padding: 48px; text-align: center; color: var(--text-muted); font-size: 14px; }

  /* ── Toast ── */
  .toast { position: fixed; top: 20px; right: 20px; z-index: 100; padding: 12px 18px; border-radius: 8px; font-size: 13px; font-weight: 500; animation: fadeIn 0.3s ease; box-shadow: 0 4px 20px #00000060; }
  .toast.success { background: #10B98120; border: 1px solid #10B981; color: #10B981; }
  .toast.error { background: #EF444420; border: 1px solid #EF4444; color: #EF4444; }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: translateY(0); } }

  /* ── Modal ── */
  .modal-overlay { position: fixed; inset: 0; background: #00000080; z-index: 200; display: flex; align-items: center; justify-content: center; padding: 20px; animation: fadeIn 0.2s ease; }
  .modal { background: var(--surface); border: 1px solid var(--border-light); border-radius: 16px; width: 100%; max-width: 560px; max-height: 90vh; overflow-y: auto; box-shadow: 0 24px 80px #00000080; }
  .modal-header { padding: 20px 24px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; }
  .modal-title { font-size: 16px; font-weight: 600; }
  .modal-close { background: none; border: none; color: var(--text-muted); font-size: 18px; cursor: pointer; padding: 4px; transition: color 0.15s; }
  .modal-close:hover { color: var(--text); }
  .modal-body { padding: 24px; display: flex; flex-direction: column; gap: 16px; }
  .modal-footer { padding: 16px 24px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 10px; }
  .btn { padding: 9px 18px; border-radius: 8px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; border: none; transition: all 0.15s; }
  .btn-primary { background: var(--accent); color: white; }
  .btn-primary:hover { background: #2563EB; }
  .btn-primary:disabled { background: var(--surface3); color: var(--text-muted); cursor: not-allowed; }
  .btn-secondary { background: var(--surface2); border: 1px solid var(--border-light); color: var(--text-muted); }
  .btn-secondary:hover { border-color: var(--accent); color: var(--text); }
  .btn-danger { background: #EF444420; border: 1px solid #EF444440; color: var(--danger); }
  .btn-danger:hover { background: var(--danger); color: white; }

  /* ── Cities table ── */
  .cities-table { width: 100%; border-collapse: collapse; }
  .cities-table th { padding: 10px 16px; text-align: left; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1px solid var(--border); white-space: nowrap; }
  .cities-table td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid var(--border); vertical-align: middle; }
  .cities-table tr:hover td { background: var(--surface2); }
  .status-badge { display: inline-block; padding: 3px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; }
  .status-badge.trialing { background: #10B98120; color: #10B981; border: 1px solid #10B98130; }
  .status-badge.active { background: #3B82F620; color: #60A5FA; border: 1px solid #3B82F630; }
  .status-badge.past_due { background: #F59E0B20; color: #F59E0B; border: 1px solid #F59E0B30; }
  .status-badge.paused { background: #64748B20; color: #64748B; border: 1px solid #64748B30; }
  .status-badge.no_trial { background: #1E2D4580; color: #475569; border: 1px solid #2A3F5F; }
  .action-btn { padding: 5px 10px; background: var(--surface2); border: 1px solid var(--border-light); border-radius: 6px; color: var(--text-muted); font-size: 11px; font-family: inherit; cursor: pointer; transition: all 0.15s; white-space: nowrap; }
  .action-btn:hover { border-color: var(--accent); color: var(--accent-light); }

  /* ── Preview widget ── */
  /* ── Preview widget ── */
  .widget-preview { background: #080D18; border: 1px solid var(--border); border-radius: 12px; padding: 20px; margin-top: 16px; }
  .widget-preview-title { font-size: 12px; color: var(--text-muted); margin-bottom: 14px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600; }
  .widget-bubble { width: 52px; height: 52px; border-radius: 50%; background: linear-gradient(135deg, #3B82F6, #0EA5E9); display: flex; align-items: center; justify-content: center; font-size: 22px; cursor: pointer; margin-left: auto; box-shadow: 0 4px 20px #3B82F640; transition: transform 0.2s; }
  .widget-bubble:hover { transform: scale(1.08); }
  .widget-chat { background: var(--surface); border: 1px solid var(--border-light); border-radius: 12px; padding: 16px; margin-bottom: 12px; font-size: 13px; line-height: 1.5; }
  .widget-chat-name { font-size: 11px; font-weight: 600; color: var(--accent-light); margin-bottom: 6px; }
`;

// ─── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState('login');
  const [user, setUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [impersonating, setImpersonating] = useState(() => {
    // Only restore impersonation if super token is still present
    const superToken = localStorage.getItem('civicbot_super_token');
    if (!superToken) {
      localStorage.removeItem('civicbot_impersonating');
      return null;
    }
    return localStorage.getItem('civicbot_impersonating') || null;
  });

  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Check for existing token or set-password token in URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    const newAccount = urlParams.get('newAccount');
    const urlCityId = urlParams.get('cityId');
    const urlCityName = urlParams.get('cityName');
    const urlEmail = urlParams.get('email');

    // New account redirect from onboarding wizard
    if (newAccount === 'true' && resetToken && urlCityId) {
      const userData = { email: urlEmail, role: 'city_admin', cityId: urlCityId, cityName: urlCityName };
      localStorage.setItem('civicbot_token', resetToken);
      localStorage.setItem('civicbot_user', JSON.stringify(userData));
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      setUser(userData);
      setPage('submissions');
      return;
    }

    // Set password flow
    if (resetToken && !newAccount) {
      setPage('set-password');
      return;
    }

    // Existing session
    const token = localStorage.getItem('civicbot_token');
    const savedUser = localStorage.getItem('civicbot_user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      setPage('submissions');
    }
  }, []);

  function handleLogin(userData) {
    setUser(userData);
    setPage('submissions');
  }

  function handleLogout() {
    localStorage.removeItem('civicbot_token');
    localStorage.removeItem('civicbot_user');
    setUser(null);
    setPage('login');
  }

  function exitImpersonation() {
    const superToken = localStorage.getItem('civicbot_super_token');
    const superUser = localStorage.getItem('civicbot_super_user');
    localStorage.setItem('civicbot_token', superToken);
    localStorage.setItem('civicbot_user', superUser);
    localStorage.removeItem('civicbot_super_token');
    localStorage.removeItem('civicbot_super_user');
    localStorage.removeItem('civicbot_impersonating');
    setImpersonating(null);
    setUser(JSON.parse(superUser));
    setPage('cities');
  }

  // Listen for navigate events from child components
  useEffect(() => {
    const handler = (e) => setPage(e.detail);
    window.addEventListener('navigate', handler);
    return () => window.removeEventListener('navigate', handler);
  }, []);

  if (page === 'login') {
    return (
      <>
        <style>{styles}</style>
        <LoginPage onLogin={handleLogin} showToast={showToast} onSignup={() => setPage('signup')} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  if (page === 'signup') {
    return (
      <>
        <style>{styles}</style>
        <SignupPage onLogin={handleLogin} onBack={() => setPage('login')} showToast={showToast} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  if (page === 'set-password') {
    return (
      <>
        <style>{styles}</style>
        <SetPasswordPage onComplete={handleLogin} showToast={showToast} />
        {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
      </>
    );
  }

  return (
    <>
      <style>{styles}</style>
      <div className="app">
        <Header user={user} onLogout={handleLogout} />
        <div className="main">
          <Sidebar page={page} setPage={setPage} user={user} />
          {impersonating && (
            <div style={{position:'fixed',top:0,left:0,right:0,zIndex:9999,background:'#F59E0B',color:'#000',padding:'10px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',fontWeight:600,fontSize:13,boxShadow:'0 2px 8px rgba(0,0,0,0.3)'}}>
              <span>👁 Logged in as <strong>{impersonating}</strong> — You are acting as this city admin</span>
              <button onClick={exitImpersonation} style={{background:'#000',color:'#fff',border:'none',borderRadius:4,padding:'6px 16px',cursor:'pointer',fontWeight:600,fontSize:12}}>✕ Exit to Super Admin</button>
            </div>
          )}
          <div className="content">
            {page === 'submissions' && <SubmissionsPage user={user} showToast={showToast} />}
            {page === 'analytics' && <AnalyticsPage user={user} showToast={showToast} />}
            {page === 'templates' && <TemplatesPage showToast={showToast} />}
            {page === 'settings' && <SettingsPage showToast={showToast} />}
            {page === 'billing' && <BillingPage user={user} showToast={showToast} onAnswersEnabled={() => {
              const updated = { ...user, isAnswersPlan: true };
              setUser(updated);
              localStorage.setItem('civicbot_user', JSON.stringify(updated));
            }} />}
            {page === 'embed' && <EmbedPage user={user} />}
            {page === 'answers' && <AnswersPage user={user} showToast={showToast} />}
            {page === 'cities' && <CitiesPage user={user} showToast={showToast} setImpersonating={setImpersonating} />}
          </div>
        </div>
      </div>
      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}


// ─── Set Password Page ────────────────────────────────────────────────────────
// ─── Tooltip Component ───────────────────────────────────────────────────────
function Tooltip({ text }) {
  const [show, setShow] = React.useState(false);
  const [pos, setPos] = React.useState({top:0, left:0});
  function handleMouseEnter(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ top: rect.bottom + 8, left: Math.min(rect.left - 120, window.innerWidth - 280) });
    setShow(true);
  }
  return (
    <span style={{position:'relative',display:'inline-flex',alignItems:'center',marginLeft:6}}>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setShow(false)}
        style={{width:16,height:16,borderRadius:'50%',background:'var(--surface2)',border:'1px solid var(--border-light)',display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:10,color:'var(--text-muted)',cursor:'help',fontWeight:700,flexShrink:0}}
      >?</span>
      {show && (
        <div style={{position:'fixed',top:pos.top,left:pos.left,background:'#1E293B',border:'1px solid var(--border2)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text2)',lineHeight:1.5,zIndex:99999,width:260,boxShadow:'0 8px 24px rgba(0,0,0,0.4)',whiteSpace:'normal'}}>
          {text}
          <div style={{position:'absolute',bottom:'100%',left:'20px',width:0,height:0,borderLeft:'6px solid transparent',borderRight:'6px solid transparent',borderBottom:'6px solid #1E293B'}} />
        </div>
      )}
    </span>
  );
}

function SetPasswordPage({ onComplete, showToast }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(true);

  const token = new URLSearchParams(window.location.search).get('token');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!password || !confirm) { setError('Please fill in both fields'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/set-password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        localStorage.setItem('civicbot_user', JSON.stringify({
          email: data.email, role: data.role || 'city_admin',
          cityId: data.cityId, cityName: data.cityName
        }));
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        showToast('Password set! Welcome to CivicBot.');
        onComplete({ email: data.email, role: data.role || 'city_admin', cityId: data.cityId, cityName: data.cityName });
      } else {
        if (data.error?.includes('expired') || data.error?.includes('Invalid')) {
          setTokenValid(false);
        }
        setError(data.error || 'Failed to set password');
      }
    } catch {
      setError('Connection error. Please try again.');
    }
    setLoading(false);
  }

  if (!token) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{textAlign:'center'}}>
          <div style={{fontSize:32, marginBottom:16}}>🔗</div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Invalid Link</div>
          <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:24}}>This link is invalid or missing a token.</div>
          <a href="/" style={{color:'var(--accent-light)', fontSize:13}}>← Back to login</a>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{textAlign:'center'}}>
          <div style={{fontSize:32, marginBottom:16}}>⏰</div>
          <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Link Expired</div>
          <div style={{fontSize:13, color:'var(--text-muted)', marginBottom:24, lineHeight:1.6}}>
            This password setup link has expired (links are valid for 48 hours).<br/>
            Please use the "Forgot your password?" link to request a new one.
          </div>
          <button className="login-btn" onClick={() => { window.history.replaceState({}, '', window.location.pathname); window.location.reload(); }}>
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">🏙️</div>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">Welcome! Set your password to access your CivicBot admin dashboard.</p>
        <form onSubmit={handleSubmit}>
          <div className="login-field">
            <label className="login-label">New Password</label>
            <input
              className="login-input"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoFocus
            />
          </div>
          <div className="login-field">
            <label className="login-label">Confirm Password</label>
            <input
              className="login-input"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter your password"
            />
          </div>
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'10px 14px', fontSize:12, color:'var(--text-muted)', marginBottom:8, lineHeight:1.6}}>
            🔒 Use at least 8 characters. Your password is encrypted and never stored in plain text.
          </div>
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? 'Setting password...' : 'Set Password & Sign In →'}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
      </div>
    </div>
  );
}

// ─── Login ─────────────────────────────────────────────────────────────────────
// ─── Signup Page ───────────────────────────────────────────────────────────────
function SignupPage({ onLogin, onBack, showToast }) {
  const [form, setForm] = useState({ cityName: '', email: '', password: '', state: '', deliveryType: 'email' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const US_STATES = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ];

  async function handleSignup(e) {
    e.preventDefault();
    if (!form.cityName || !form.email || !form.password || !form.state) { setError('All fields are required'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cityName: form.cityName, email: form.email, password: form.password, state: form.state, isCityworks: form.deliveryType === 'cityworks' }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        localStorage.setItem('civicbot_user', JSON.stringify({ email: data.email, role: 'city_admin', cityId: data.cityId, cityName: data.cityName, isAnswersPlan: false }));
        showToast('Welcome to CivicBot! Your 30-day trial has started.');
        onLogin({ email: data.email, role: 'city_admin', cityId: data.cityId, cityName: data.cityName, isAnswersPlan: false });
      } else {
        setError(data.error || 'Signup failed — try again');
      }
    } catch {
      setError('Cannot connect to CivicBot server');
    }
    setLoading(false);
  }

  return (
    <div className="login-wrap">
      <div className="login-card" style={{maxWidth:460}}>
        <div className="login-logo">
          <div className="login-logo-icon">🏙️</div>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">Start your 30-day free trial — no credit card required.</p>
        <form onSubmit={handleSignup}>
          <div className="login-field">
            <label className="login-label">City / Organization Name</label>
            <input className="login-input" value={form.cityName} onChange={e => setForm(f=>({...f,cityName:e.target.value}))} placeholder="City of Springfield" autoFocus />
          </div>
          <div className="login-field">
            <label className="login-label">State</label>
            <select className="login-input" value={form.state} onChange={e => setForm(f=>({...f,state:e.target.value}))} style={{cursor:'pointer'}}>
              <option value="">Select state...</option>
              {US_STATES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
            </select>
          </div>
          <div className="login-field">
            <label className="login-label">Admin Email</label>
            <input className="login-input" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="admin@yourcity.gov" />
          </div>
          <div className="login-field">
            <label className="login-label">Password</label>
            <input className="login-input" type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} placeholder="Min. 8 characters" />
          </div>
          <div className="login-field">
            <label className="login-label">How will you receive service requests?</label>
            <div style={{display:'flex',gap:8,marginTop:4}}>
              <button type="button" onClick={() => setForm(f=>({...f,deliveryType:'email'}))} style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${form.deliveryType==='email'?'var(--accent)':'var(--border)'}`,background:form.deliveryType==='email'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'center'}}>
                <div style={{fontSize:18,marginBottom:2}}>📧</div>
                <div style={{fontSize:12,fontWeight:600,color:form.deliveryType==='email'?'var(--accent-light)':'var(--text)'}}>Email</div>
              </button>
              <button type="button" onClick={() => setForm(f=>({...f,deliveryType:'cityworks'}))} style={{flex:1,padding:'10px 8px',borderRadius:8,border:`2px solid ${form.deliveryType==='cityworks'?'var(--accent)':'var(--border)'}`,background:form.deliveryType==='cityworks'?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',textAlign:'center'}}>
                <div style={{fontSize:18,marginBottom:2}}>🔗</div>
                <div style={{fontSize:12,fontWeight:600,color:form.deliveryType==='cityworks'?'var(--accent-light)':'var(--text)'}}>Cityworks</div>
              </button>
            </div>
          </div>
          <button className="login-btn" type="submit" disabled={loading} style={{marginTop:8}}>
            {loading ? 'Creating account...' : 'Start Free Trial →'}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
        <div className="login-forgot" style={{textAlign:'center',marginTop:16}}>
          <a onClick={onBack} style={{cursor:'pointer'}}>← Back to Sign In</a>
        </div>
      </div>
    </div>
  );
}

function LoginPage({ onLogin, showToast, onSignup }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!email || !password) { setError('Please enter your email and password'); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (data.success) {
        if (data.token) localStorage.setItem('civicbot_token', data.token);
        localStorage.setItem('civicbot_user', JSON.stringify({ email: data.email, role: data.role, cityId: data.cityId, cityName: data.cityName, isAnswersPlan: data.isAnswersPlan || false }));
        onLogin({ email: data.email, role: data.role, cityId: data.cityId, cityName: data.cityName, isAnswersPlan: data.isAnswersPlan || false });
        if (data.cityId) {
          fetch(API_BASE + '/api/config/' + data.cityId).then(r=>r.json()).then(cfg => {
            if (cfg.success && cfg.config && cfg.config.logoUrl) {
              const u = JSON.parse(localStorage.getItem('civicbot_user')||'{}');
              u.cityLogo = cfg.config.logoUrl;
              localStorage.setItem('civicbot_user', JSON.stringify(u));
            }
          }).catch(()=>{});
        }
        showToast('Welcome back!');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Cannot connect to CivicBot server');
    }
    setLoading(false);
  }

  async function handleForgot(e) {
    e.preventDefault();
    if (!email) { setError('Please enter your email'); return; }
    setLoading(true); setError('');
    try {
      await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      setForgotSent(true);
    } catch {
      setError('Could not send reset email');
    }
    setLoading(false);
  }

  if (forgotSent) {
    return (
      <div className="login-wrap">
        <div className="login-card">
          <div className="login-logo">
            <div className="login-logo-icon">🏙️</div>
            <div className="login-logo-text">Civic<span>Bot</span></div>
          </div>
          <div style={{textAlign:'center', padding:'20px 0'}}>
            <div style={{fontSize:32, marginBottom:16}}>📧</div>
            <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Check your inbox</div>
            <div style={{fontSize:13, color:'var(--text-muted)', lineHeight:1.6}}>We sent a password reset link to <strong>{email}</strong>. Click the link in the email to set your password.</div>
            <button className="login-btn" style={{marginTop:24}} onClick={() => { setForgotMode(false); setForgotSent(false); }}>Back to Sign In</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-wrap">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">🏙️</div>
          <div className="login-logo-text">Civic<span>Bot</span></div>
        </div>
        <p className="login-subtitle">
          {forgotMode ? 'Enter your email to receive a password reset link.' : 'Admin portal for managing your city\'s AI-powered service request chatbot.'}
        </p>
        <form onSubmit={forgotMode ? handleForgot : handleLogin}>
          <div className="login-field">
            <label className="login-label">Email</label>
            <input className="login-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@yourcity.gov" autoFocus />
          </div>
          {!forgotMode && (
            <div className="login-field">
              <label className="login-label">Password</label>
              <input className="login-input" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
          )}
          <button className="login-btn" type="submit" disabled={loading}>
            {loading ? (forgotMode ? 'Sending...' : 'Signing in...') : (forgotMode ? 'Send Reset Link' : 'Sign In')}
          </button>
          {error && <p className="login-error">{error}</p>}
        </form>
        <div className="login-forgot">
          {forgotMode
            ? <a onClick={() => { setForgotMode(false); setError(''); }}>← Back to Sign In</a>
            : <a onClick={() => { setForgotMode(true); setError(''); }}>Forgot your password?</a>
          }
        </div>
        {!forgotMode && onSignup && (
          <div style={{textAlign:'center', marginTop:20, fontSize:13, color:'var(--text-muted)'}}>
            New to CivicBot?{' '}
            <a onClick={onSignup} style={{color:'var(--accent-light)', cursor:'pointer', fontWeight:600}}>Start free trial →</a>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Header ────────────────────────────────────────────────────────────────────
function Header({ user, onLogout }) {
  return (
    <div className="header">
      <div className="header-logo">
        <div className="header-logo-icon">🏙️</div>
        CivicBot Admin
        {user?.cityName && <span style={{fontSize:12, color:'var(--text-muted)', fontWeight:400, marginLeft:8}}>— {user.cityName}</span>}
      </div>
      <div className="header-right">
        <div className="header-status">
          <div className="status-dot" />
          {user?.role === 'super_admin' ? 'Centricity GIS · Super Admin' : 'Connected'}
        </div>
        <span style={{fontSize:12, color:'var(--text-muted)'}}>{user?.email}</span>
        <button className="logout-btn" onClick={onLogout}>Sign Out</button>
      </div>
    </div>
  );
}

// ─── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ page, setPage, user }) {
  const cityItems = [
    { id: 'submissions', icon: '📋', label: 'Submissions' },
    { id: 'analytics', icon: '📊', label: 'Analytics' },
    { id: 'templates', icon: '☑️', label: 'SR Templates' },
    { id: 'embed', icon: '🔗', label: 'Embed Code' },
    { id: 'billing', icon: '💳', label: 'Billing' },
    { id: 'settings', icon: '⚙️', label: 'Settings' },
    ...(user?.isAnswersPlan ? [{ id: 'answers', icon: '🤖', label: 'CivicBot Answers' }] : []),
  ];
  const adminItems = [
    { id: 'cities', icon: '🏙️', label: 'All Cities' },
  ];
  return (
    <div className="sidebar">
      {user?.role === 'super_admin' && <>
        <div className="sidebar-label">Centricity Admin</div>
        {adminItems.map(item => (
          <div key={item.id} className={`sidebar-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
            <span>{item.icon}</span>
            <span>{item.label}</span>
          </div>
        ))}
      </>}
      <div className="sidebar-label">Navigation</div>
      {cityItems.map(item => (
        <div key={item.id} className={`sidebar-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
          <span>{item.icon}</span>
          <span>{item.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Submissions Page ──────────────────────────────────────────────────────────

// ─── ANALYTICS PAGE ───────────────────────────────────────────────────────────
function AnalyticsPage({ user, showToast }) {
  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [usage, setUsage] = React.useState(null);
  const [dateRange, setDateRange] = React.useState('month');

  const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#84CC16','#F97316'];

  React.useEffect(() => {
    const cityId = user?.cityId;
    if (!cityId) return;
    apiFetch('/api/submissions?cityId=' + cityId)
      .then(r => r.json())
      .then(data => { if (data.success) setSubmissions(data.submissions || []); })
      .catch(() => showToast('Failed to load analytics', 'error'))
      .finally(() => setLoading(false));
    apiFetch('/api/usage/' + cityId)
      .then(r => r.json())
      .then(data => { if (data.success) setUsage(data); })
      .catch(() => {});
  }, [user]);

  function getFilteredSubs() {
    const now = new Date();
    return submissions.filter(s => {
      if (!s.submitted_at) return false;
      const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
      if (dateRange === 'week') { const start = new Date(now); start.setDate(now.getDate() - 7); return d >= start; }
      if (dateRange === 'month') { const start = new Date(now.getFullYear(), now.getMonth(), 1); return d >= start; }
      if (dateRange === 'year') { return d.getFullYear() === now.getFullYear(); }
      return true;
    });
  }

  function getByType(subs) {
    const counts = {};
    subs.forEach(s => { const t = s.sr_type || 'Unknown'; counts[t] = (counts[t] || 0) + 1; });
    return Object.entries(counts).map(([name, count]) => ({ name, count })).sort((a,b) => b.count - a.count);
  }

  function getByDay(subs) {
    const counts = {};
    subs.forEach(s => {
      if (!s.submitted_at) return;
      const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
      const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([date, count]) => ({ date, count }));
  }

  function getByDelivery(subs) {
    const email = subs.filter(s => s.delivery_method === 'email').length;
    const cw = subs.filter(s => s.delivery_method === 'cityworks').length;
    return [{ name: 'Email', count: email }, { name: 'Cityworks', count: cw }].filter(d => d.count > 0);
  }

  function MiniPie({ data }) {
    if (!data || data.length === 0) return <div style={{textAlign:'center',padding:'20px',color:'var(--text-muted)',fontSize:13}}>No data</div>;
    const total = data.reduce((s,d) => s + d.count, 0);
    let angle = 0;
    const size = 140, cx = 70, cy = 70, r = 55;
    const toRad = deg => (deg - 90) * Math.PI / 180;
    const slices = data.map((d, i) => {
      const pct = d.count / total;
      const start = angle;
      angle += pct * 360;
      const end = angle;
      if (end - start >= 360) { return <circle key={i} cx={cx} cy={cy} r={r} fill={COLORS[i % COLORS.length]} />; }
      const x1 = cx + r * Math.cos(toRad(start)), y1 = cy + r * Math.sin(toRad(start));
      const x2 = cx + r * Math.cos(toRad(end)), y2 = cy + r * Math.sin(toRad(end));
      const large = end - start > 180 ? 1 : 0;
      return <path key={i} d={"M "+cx+" "+cy+" L "+x1+" "+y1+" A "+r+" "+r+" 0 "+large+" 1 "+x2+" "+y2+" Z"} fill={COLORS[i % COLORS.length]} />;
    });
    return (
      <div style={{display:'flex',gap:16,alignItems:'center',flexWrap:'wrap'}}>
        <svg width={size} height={size}>{slices}</svg>
        <div style={{display:'flex',flexDirection:'column',gap:6}}>
          {data.slice(0,6).map((d,i) => (
            <div key={i} style={{display:'flex',alignItems:'center',gap:6,fontSize:12}}>
              <div style={{width:10,height:10,borderRadius:2,background:COLORS[i % COLORS.length],flexShrink:0}} />
              <span style={{color:'var(--text-muted)'}}>{d.name}</span>
              <span style={{fontWeight:600,marginLeft:'auto',paddingLeft:8}}>{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  function BarChart({ data }) {
    if (!data || data.length === 0) return <div style={{textAlign:'center',padding:'20px',color:'var(--text-muted)',fontSize:13}}>No data</div>;
    const max = Math.max(...data.map(d => d.count), 1);
    return (
      <div style={{display:'flex',alignItems:'flex-end',gap:4,height:80,paddingTop:8}}>
        {data.slice(-14).map((d,i) => (
          <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
            <div style={{width:'100%',background:'#3B82F6',borderRadius:'2px 2px 0 0',height: Math.max((d.count/max)*72, 2) +'px',opacity:0.85}} title={d.date+': '+d.count} />
            <span style={{fontSize:9,color:'var(--text-muted)',transform:'rotate(-45deg)',transformOrigin:'center',whiteSpace:'nowrap'}}>{d.date}</span>
          </div>
        ))}
      </div>
    );
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading analytics...</div>;

  const filtered = getFilteredSubs();
  const byType = getByType(filtered);
  const byDay = getByDay(filtered);
  const byDelivery = getByDelivery(filtered);
  const total = filtered.length;
  const allTime = submissions.length;
  const thisMonth = submissions.filter(s => {
    if (!s.submitted_at) return false;
    const d = new Date(s.submitted_at.includes('T') ? s.submitted_at : s.submitted_at.replace(' ', 'T') + 'Z');
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;
  const topType = byType[0]?.name || '—';
  const photoCount = filtered.filter(s => s.photo_attached).length;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Analytics</div>
          <div className="page-subtitle">Service request insights for {user?.cityName}</div>
        </div>
        <div style={{display:'flex',gap:8}}>
          {['week','month','year','all'].map(r => (
            <button key={r} onClick={() => setDateRange(r)}
              style={{padding:'6px 14px',borderRadius:6,border:'1px solid var(--border)',background: dateRange===r ? '#3B82F6' : 'var(--surface)',color: dateRange===r ? '#fff' : 'var(--text)',cursor:'pointer',fontSize:13,fontWeight: dateRange===r ? 600 : 400}}>
              {r === 'all' ? 'All Time' : r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Stat Cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:16,marginBottom:24}}>
        {[
          { label: 'This Period', value: total, icon: '📋', color: '#3B82F6' },
          { label: 'This Month', value: thisMonth, icon: '📅', color: '#10B981' },
          { label: 'All Time', value: allTime, icon: '🏆', color: '#8B5CF6' },
          { label: 'Top Issue', value: topType, icon: '🔝', color: '#F59E0B', small: true },
          { label: 'With Photos', value: photoCount, icon: '📷', color: '#06B6D4' },
        ].map((card, i) => (
          <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'16px 20px',borderTop:'3px solid '+card.color}}>
            <div style={{fontSize:20,marginBottom:6}}>{card.icon}</div>
            <div style={{fontSize: card.small ? 14 : 28, fontWeight:700, color:'var(--text)', lineHeight:1.1, marginBottom:4}}>{card.value}</div>
            <div style={{fontSize:12,color:'var(--text-muted)',fontWeight:500}}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:24}}>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Submissions Over Time</div>
          <BarChart data={byDay} />
        </div>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Delivery Method</div>
          <MiniPie data={byDelivery} />
        </div>
      </div>

      {/* Issue Types */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20,marginBottom:24}}>
        <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Issues by Type</div>
        <div style={{display:'grid',gridTemplateColumns:'auto 1fr',gap:16,alignItems:'center'}}>
          <MiniPie data={byType.slice(0,8)} />
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {byType.slice(0,10).map((t,i) => {
              const pct = total > 0 ? Math.round(t.count/total*100) : 0;
              return (
                <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                  <div style={{width:10,height:10,borderRadius:2,background:COLORS[i % COLORS.length],flexShrink:0}} />
                  <span style={{fontSize:13,flex:1,color:'var(--text)'}}>{t.name}</span>
                  <div style={{width:120,height:6,background:'var(--surface2)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{width:pct+'%',height:'100%',background:COLORS[i % COLORS.length],borderRadius:3}} />
                  </div>
                  <span style={{fontSize:12,color:'var(--text-muted)',width:30,textAlign:'right'}}>{t.count}</span>
                  <span style={{fontSize:11,color:'var(--text-muted)',width:32,textAlign:'right'}}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Usage / Plan */}
      {usage && (
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:20}}>
          <div style={{fontWeight:600,fontSize:14,marginBottom:16}}>Plan Usage — {new Date().toLocaleString('en-US',{month:'long',year:'numeric'})}</div>
          <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
            <div style={{flex:1,minWidth:200}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                <span style={{fontSize:13,color:'var(--text-muted)'}}>Submissions used</span>
                <span style={{fontSize:13,fontWeight:600}}>{usage.monthCount || 0} / {usage.limit || '—'}</span>
              </div>
              <div style={{height:8,background:'var(--surface2)',borderRadius:4,overflow:'hidden'}}>
                <div style={{
                  height:'100%',
                  borderRadius:4,
                  background: (usage.monthCount||0)/(usage.limit||1) > 0.9 ? '#EF4444' : (usage.monthCount||0)/(usage.limit||1) > 0.7 ? '#F59E0B' : '#10B981',
                  width: Math.min(100, Math.round((usage.monthCount||0)/(usage.limit||1)*100)) + '%'
                }} />
              </div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:12,color:'var(--text-muted)'}}>Plan</div>
              <div style={{fontWeight:600,fontSize:14}}>{usage.planName || '—'}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

function SubmissionsPage({ user, showToast }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [monthCount, setMonthCount] = useState(0);
  const [usage, setUsage] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [adminDashboard, setAdminDashboard] = useState(null);
  const [adminSearch, setAdminSearch] = useState('');
  function getWeekStart() { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0,10); }
  function getToday() { return new Date().toISOString().slice(0,10); }
  const [dateFrom, setDateFrom] = useState(getWeekStart);
  const [dateTo, setDateTo] = useState(getToday);
  const [descSearch, setDescSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('week');

  useEffect(() => {
    const cityId = user?.cityId;
    // Super admin — load global dashboard instead
    if (!cityId && user?.role === 'super_admin') {
      apiFetch('/api/admin/submissions/all')
        .then(r => r.json())
        .then(data => { if (data.success) setAdminDashboard(data); })
        .catch(() => showToast('Failed to load dashboard', 'error'))
        .finally(() => setLoading(false));
      return;
    }
    if (!cityId) return;

    apiFetch(`/api/submissions?cityId=${cityId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setSubmissions(data.submissions);
          setTotal(data.total);
          setMonthCount(data.monthCount);
        } else {
          showToast('Failed to load submissions', 'error');
        }
      })
      .catch(() => showToast('Failed to load submissions', 'error'))
      .finally(() => setLoading(false));

    // Fetch usage metering
    if (cityId) {
      apiFetch(`/api/usage/${cityId}`)
        .then(r => r.json())
        .then(data => { if (data.success) setUsage(data); })
        .catch(() => {});
    }
  }, [user, showToast]);

  const filtered = submissions.filter(s => {
    const matchSearch = !search ||
      (s.confirmation_number || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.sr_type || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.address || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.caller_name || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'ALL' || s.delivery_method === filter.toLowerCase();
    const matchDateFrom = !dateFrom || (s.submitted_at || '') >= dateFrom;
    const matchDateTo = !dateTo || (s.submitted_at || '') <= dateTo + 'T23:59:59';
    const matchDesc = !descSearch || (s.description || '').toLowerCase().includes(descSearch.toLowerCase()) || (s.internal_notes || '').toLowerCase().includes(descSearch.toLowerCase());
    return matchSearch && matchFilter && matchDateFrom && matchDateTo && matchDesc;
  });

  function formatDate(dt) {
    if (!dt) return '—';
    const normalized = dt.includes('T') ? dt : dt.replace(' ', 'T') + 'Z';
    return new Date(normalized).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function exportCSV() {
    const headers = ['Confirmation #','SR Type','Address','Submitted By','Phone','Email','Delivery','Date','Completed','Notes'];
    const rows = filtered.map(function(s) {
      return [
        s.confirmation_number || '', s.sr_type || '',
        s.geo_address || s.address || '', s.caller_name || '',
        s.caller_phone || '', s.caller_email || '',
        s.delivery_method || '',
        s.submitted_at ? formatDate(s.submitted_at) : '',
        s.completed ? 'Yes' : 'No', s.internal_notes || ''
      ];
    });
    const nl = String.fromCharCode(10);
    const csv = [headers].concat(rows).map(function(r) {
      return r.map(function(v) { return '"' + String(v).replace(/"/g, '""') + '"'; }).join(',');
    }).join(nl);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'submissions-' + new Date().toISOString().slice(0,10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  function applyQuickFilter(val) {
    setQuickFilter(val);
    const today = new Date();
    const pad = d => d.toISOString().slice(0,10);
    if (val === 'today') { setDateFrom(pad(today)); setDateTo(pad(today)); }
    else if (val === 'week') { const s = new Date(today); s.setDate(today.getDate() - today.getDay()); setDateFrom(pad(s)); setDateTo(pad(today)); }
    else if (val === 'month') { const s = new Date(today.getFullYear(), today.getMonth(), 1); setDateFrom(pad(s)); setDateTo(pad(today)); }
    else if (val === 'year') { setDateFrom(today.getFullYear() + '-01-01'); setDateTo(pad(today)); }
    else if (val === 'all') { setDateFrom(''); setDateTo(''); }
  }

  async function updateSubmission(id, updates) {
    setSubmissions(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    try {
      await apiFetch(`/api/submissions/${id}`, { method: 'PATCH', body: JSON.stringify(updates) });
    } catch { showToast('Failed to save', 'error'); }
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading submissions...</div>;

  // Super Admin Global Dashboard
  if (user?.role === 'super_admin' && !user?.cityId) {
    if (!adminDashboard) return <div className="loading"><div className="spinner" />Loading dashboard...</div>;
    const { stats, byCity, submissions: allSubs } = adminDashboard;
    const COLORS = ['#3B82F6','#10B981','#F59E0B','#EF4444','#8B5CF6','#06B6D4','#84CC16','#F97316'];

    const PieChart = ({ data, title }) => {
      if (!data || data.length === 0) return (
        <div style={{textAlign:'center',padding:'20px 0'}}>
          <div style={{fontSize:13,color:'var(--text-muted)'}}>No data</div>
        </div>
      );
      const total = data.reduce((s, d) => s + d.count, 0);
      let angle = 0;
      const slices = data.map((d, i) => {
        const pct = d.count / total;
        const startAngle = angle;
        angle += pct * 360;
        return { ...d, pct, startAngle, endAngle: angle, color: COLORS[i % COLORS.length] };
      });
      const size = 160;
      const cx = size / 2, cy = size / 2, r = 68;
      const toRad = deg => (deg - 90) * Math.PI / 180;
      const arc = (start, end, color) => {
        if (end - start >= 360) end = 359.99;
        const x1 = cx + r * Math.cos(toRad(start));
        const y1 = cy + r * Math.sin(toRad(start));
        const x2 = cx + r * Math.cos(toRad(end));
        const y2 = cy + r * Math.sin(toRad(end));
        const large = end - start > 180 ? 1 : 0;
        return <path key={color+start} d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`} fill={color} />;
      };
      return (
        <div>
          <div style={{fontSize:12,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:1,marginBottom:8}}>{title}</div>
          <div style={{display:'flex',alignItems:'center',gap:16}}>
            <svg width={size} height={size} style={{flexShrink:0}}>
              {slices.map(s => arc(s.startAngle, s.endAngle, s.color))}
              <circle cx={cx} cy={cy} r={38} fill="var(--surface)" />
              <text x={cx} y={cy+1} textAnchor="middle" dominantBaseline="middle" style={{fontSize:14,fontWeight:700,fill:'var(--text)'}}>{total}</text>
            </svg>
            <div style={{fontSize:11,lineHeight:1.8}}>
              {slices.map((s,i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:6}}>
                  <div style={{width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0}} />
                  <span style={{color:'var(--text-muted)'}}>{s.city_name}</span>
                  <span style={{color:'var(--text)',fontWeight:600,marginLeft:'auto',paddingLeft:8}}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    const filteredSubs = allSubs.filter(s =>
      !adminSearch ||
      (s.city_name||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.confirmation_number||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.sr_type||'').toLowerCase().includes(adminSearch.toLowerCase()) ||
      (s.address||'').toLowerCase().includes(adminSearch.toLowerCase())
    );

    return (
      <div>
        <div className="page-header">
          <div className="page-title">Global Submissions Dashboard</div>
          <div className="page-subtitle">All service requests across all cities</div>
        </div>

        {/* Stats Row */}
        <div className="stats-row" style={{marginBottom:24}}>
          <div className="stat-card"><div className="stat-label">Today</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{stats.today}</div><div className="stat-sub">submissions</div></div>
          <div className="stat-card"><div className="stat-label">This Week</div><div className="stat-value" style={{color:'var(--success)'}}>{stats.week}</div><div className="stat-sub">last 7 days</div></div>
          <div className="stat-card"><div className="stat-label">This Month</div><div className="stat-value" style={{color:'#F59E0B'}}>{stats.month}</div><div className="stat-sub">{new Date().toLocaleString('default',{month:'long'})}</div></div>
          <div className="stat-card"><div className="stat-label">This Year</div><div className="stat-value">{stats.year}</div><div className="stat-sub">{new Date().getFullYear()}</div></div>
        </div>

        {/* Pie Charts */}
        <div className="card" style={{marginBottom:24}}>
          <div className="card-header"><div className="card-title">Submissions by City</div></div>
          <div className="card-body">
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:24}}>
              <PieChart data={byCity.today} title="Today" />
              <PieChart data={byCity.week} title="This Week" />
              <PieChart data={byCity.month} title="This Month" />
              <PieChart data={byCity.year} title="This Year" />
            </div>
          </div>
        </div>

        {/* All Submissions Table */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">All Submissions</div>
            <input className="search-input" placeholder="Search by city, type, address..." value={adminSearch} onChange={e => setAdminSearch(e.target.value)} style={{width:280}} />
          </div>
          <div style={{overflowX:'auto'}}>
            {filteredSubs.length === 0 ? (
              <div className="empty">No submissions found.</div>
            ) : (
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>City</th>
                    <th>Confirmation #</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Submitted By</th>
                    <th>Delivery</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubs.map(s => (
                    <tr key={s.id}>
                      <td style={{fontWeight:600,fontSize:12}}>{s.city_name}</td>
                      <td style={{fontFamily:'monospace',fontSize:12,color:'var(--accent-light)'}}>{s.confirmation_number}</td>
                      <td style={{fontSize:12}}>{s.sr_type}</td>
                      <td style={{fontSize:12,color:'var(--text-muted)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.address}</td>
                      <td style={{fontSize:12}}>{s.caller_name || 'Anonymous'}</td>
                      <td><span className={'delivery-badge ' + (s.delivery_method==='email'?'email':'cityworks')}>{s.delivery_method==='email'?'📧 Email':'🔗 CW'}</span></td>
                      <td style={{fontSize:11,color:'var(--text-muted)'}}>{formatDate(s.submitted_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Submissions</div>
        <div className="page-subtitle">All service requests submitted through CivicBot</div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">This Month</div>
          <div className="stat-value" style={{color:'var(--accent-light)'}}>{monthCount}</div>
          <div className="stat-sub">Submissions in {new Date().toLocaleString('default', {month:'long'})}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Total Submissions</div>
          <div className="stat-value">{total}</div>
          <div className="stat-sub">All time</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Delivery Methods</div>
          <div className="stat-value" style={{color:'var(--success)', fontSize:18, marginTop:10}}>
            📧 {submissions.filter(s => s.delivery_method === 'email').length} &nbsp;
            🔗 {submissions.filter(s => s.delivery_method === 'cityworks').length}
          </div>
          <div className="stat-sub">Email · Cityworks</div>
        </div>
      </div>

      {usage && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header">
            <div className="card-title">📊 Monthly Usage</div>
            <span style={{fontSize:12, color:'var(--text-muted)'}}>{new Date().toLocaleString('default', {month:'long', year:'numeric'})}</span>
          </div>
          <div className="card-body">
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10}}>
              <span style={{fontSize:13, color:'var(--text)'}}>
                <strong style={{fontSize:20, color: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--accent-light)'}}>{usage.monthCount}</strong>
                <span style={{color:'var(--text-muted)'}}> of {usage.limit} submissions used</span>
              </span>
              <span style={{fontSize:13, fontWeight:600, color: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--success)'}}>
                {usage.percent}%
              </span>
            </div>
            <div style={{background:'var(--surface2)', borderRadius:8, height:14, overflow:'hidden', marginBottom:10}}>
              <div style={{
                height:'100%',
                width:`${Math.min(usage.percent, 100)}%`,
                borderRadius:8,
                background: usage.percent >= 100 ? 'var(--danger)' : usage.percent >= 80 ? 'var(--warning)' : 'var(--accent)',
                transition:'width 0.4s ease'
              }} />
            </div>
            <div style={{display:'flex', justifyContent:'space-between', fontSize:12, color:'var(--text-muted)'}}>
              <span>{usage.remaining} submissions remaining this month</span>
              {usage.percent >= 80 && usage.percent < 100 && <span style={{color:'var(--warning)'}}>⚠️ Approaching limit — consider upgrading</span>}
              {usage.percent >= 100 && <span style={{color:'var(--danger)'}}>🚫 Limit reached — submissions are blocked</span>}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div className="card-title">Service Request Log</div>
          <div style={{display:'flex', gap:8}}>
            <button className={`filter-btn ${filter==='ALL'?'active':''}`} onClick={() => setFilter('ALL')}>All</button>
            <button className={`filter-btn ${filter==='EMAIL'?'active':''}`} onClick={() => setFilter('EMAIL')}>📧 Email</button>
            <button className={`filter-btn ${filter==='CITYWORKS'?'active':''}`} onClick={() => setFilter('CITYWORKS')}>🔗 Cityworks</button>
          </div>
        </div>
        <div className="card-body" style={{padding:0}}>
          <div style={{padding:'16px 24px', borderBottom:'1px solid var(--border)', display:'flex', flexDirection:'column', gap:10}}>
            <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
              <input className="search-input" placeholder="Search by confirmation #, type, address, or name..." value={search} onChange={e => setSearch(e.target.value)} style={{flex:1, minWidth:220}} />
              <input className="search-input" placeholder="🔍 Search description, comments, notes..." value={descSearch} onChange={e => setDescSearch(e.target.value)} style={{flex:1, minWidth:200}} />
              <select className="search-input" value={quickFilter} onChange={e => applyQuickFilter(e.target.value)} style={{width:160, cursor:'pointer'}}>
                <option value="today">📅 Today</option>
                <option value="week">📅 This Week</option>
                <option value="month">📅 This Month</option>
                <option value="year">📅 This Year</option>
                <option value="all">📅 All Time</option>
                <option value="custom">📅 Custom Range</option>
              </select>
            </div>
            <div style={{display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
              <span style={{fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap'}}>Date range:</span>
              <input type="date" className="search-input" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setQuickFilter('custom'); }} style={{width:160}} />
              <span style={{fontSize:12, color:'var(--text-muted)'}}>→</span>
              <input type="date" className="search-input" value={dateTo} onChange={e => { setDateTo(e.target.value); setQuickFilter('custom'); }} style={{width:160}} />
              <div style={{marginLeft:'auto', display:'flex', alignItems:'center', gap:8}}>
                <span style={{fontSize:12, fontWeight:600, color:'var(--accent-light)', background:'var(--accent-dim)', padding:'4px 12px', borderRadius:20, border:'1px solid var(--accent)30', whiteSpace:'nowrap'}}>
                  {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                </span>
                <button onClick={exportCSV} style={{padding:'6px 12px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all 0.15s'}} onMouseOver={e=>e.currentTarget.style.borderColor='var(--accent)'} onMouseOut={e=>e.currentTarget.style.borderColor='var(--border-light)'}>📥 Export CSV</button>
                <button onClick={() => window.print()} style={{padding:'6px 12px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', fontSize:11, cursor:'pointer', fontFamily:'inherit', whiteSpace:'nowrap', transition:'all 0.15s'}} onMouseOver={e=>e.currentTarget.style.borderColor='var(--accent)'} onMouseOut={e=>e.currentTarget.style.borderColor='var(--border-light)'}>🖨️ Print / PDF</button>
              </div>
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="empty">
              {submissions.length === 0 ? 'No submissions yet — they\'ll appear here as residents use CivicBot.' : 'No submissions match your search.'}
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table className="submissions-table">
                <thead>
                  <tr>
                    <th>Confirmation #</th>
                    <th>Type</th>
                    <th>Address</th>
                    <th>Submitted By</th>
                    <th>Delivery</th>
                    <th>Date</th>
                    <th style={{textAlign:'center'}}>Done</th>
                    <th>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <tr key={s.id} className={s.completed ? 'completed-row' : ''}>
                      <td><span style={{fontFamily:'monospace', fontSize:12, color:'var(--accent-light)'}}>{s.confirmation_number}</span></td>
                      <td style={{maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{s.sr_type || '—'}</td>
                      <td style={{maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', color:'var(--text-dim)', fontSize:12}}>{s.geo_address || s.address || '—'}</td>
                      <td style={{fontSize:12, color:'var(--text-dim)'}}>{s.caller_name && s.caller_name !== 'Anonymous' ? s.caller_name : <span style={{color:'var(--text-muted)'}}>Anonymous</span>}</td>
                      <td>
                        <span className={`delivery-badge ${s.delivery_method}`}>
                          {s.delivery_method === 'email' ? '📧 Email' : '🔗 Cityworks'}
                        </span>
                      </td>
                      <td style={{fontSize:12, color:'var(--text-muted)', whiteSpace:'nowrap'}}>{formatDate(s.submitted_at)}</td>
                      <td style={{textAlign:'center', padding:'12px 8px'}}>
                        <input type="checkbox" className="sub-check" checked={!!s.completed} onChange={e => updateSubmission(s.id, {completed: e.target.checked ? 1 : 0})} />
                      </td>
                      <td style={{padding:'8px 12px', minWidth:160}}>
                        <input className="notes-input" defaultValue={s.internal_notes || ''} maxLength={250} placeholder="Add note..." onBlur={e => { if (e.target.value !== (s.internal_notes || '')) updateSubmission(s.id, {internal_notes: e.target.value}); }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Billing Page ──────────────────────────────────────────────────────────────
function BillingPage({ user, showToast, onAnswersEnabled }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('annual');
  const [upgrading, setUpgrading] = useState(null);
  const [addingAnswers, setAddingAnswers] = useState(false);

  async function loadBillingData() {
    try {
      const r = await apiFetch(`/api/billing/status/${user.cityId}`);
      const data = await r.json();
      if (data.success) setBilling(data);
    } catch (e) { /* ignore */ }
  }

  useEffect(() => {
    if (!user?.cityId) { setLoading(false); return; }
    apiFetch(`/api/billing/status/${user.cityId}`)
      .then(r => r.json())
      .then(data => { if (data.success) setBilling(data); })
      .catch(() => showToast('Failed to load billing info', 'error'))
      .finally(() => setLoading(false));
  }, [user, showToast]);

  async function openPortal() {
    try {
      const res = await apiFetch('/api/billing/portal', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, returnUrl: window.location.href }),
      });
      const data = await res.json();
      if (data.success) window.open(data.url, '_blank');
      else showToast('Could not open billing portal', 'error');
    } catch { showToast('Could not open billing portal', 'error'); }
  }

  async function handleUpgrade(priceId, planName) {
    if (!window.confirm('Switch to ' + planName + '? This will take effect immediately.')) return;
    setUpgrading(priceId);
    try {
      const res = await apiFetch('/api/billing/upgrade', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, priceId }),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Plan updated to ' + data.planName);
        setBilling(prev => ({ ...prev, currentPriceId: priceId, planName: data.planName }));
      } else if (data.isDowngradeBlock) {
        showToast(data.error, 'error');
      } else {
        showToast(data.error || 'Upgrade failed', 'error');
      }
    } catch { showToast('Upgrade failed — try again', 'error'); }
    setUpgrading(null);
  }

  async function handleAddAnswers() {
    const onTrial = billing?.status === 'trialing';
    const msg = onTrial
      ? 'Add CivicBot Answers to your trial? Answers pricing will apply when your trial converts — no charge until then.'
      : 'Add CivicBot Answers to your subscription? This will be billed immediately at the prorated rate for your current billing period.';
    if (!window.confirm(msg)) return;
    setAddingAnswers(true);
    try {
      const res = await apiFetch('/api/billing/add-answers', { method: 'POST', body: JSON.stringify({ cityId: user.cityId }) });
      const data = await res.json();
      if (data.success) {
        await loadBillingData();
        if (onAnswersEnabled) onAnswersEnabled();
        showToast('CivicBot Answers enabled! Navigate to CivicBot Answers in the left menu to set up your knowledge base.');
      } else {
        showToast(data.error || 'Failed to add Answers', 'error');
        await loadBillingData();
      }
    } catch (e) { showToast('Failed to add Answers — check connection and try again', 'error'); }
    setAddingAnswers(false);
  }

  async function handleStartTrial(priceId, planName) {
    setUpgrading(priceId);
    try {
      const res = await apiFetch('/api/billing/start-trial', {
        method: 'POST',
        body: JSON.stringify({ cityId: user.cityId, email: user.email, cityName: user.cityName, isCityworks: billing?.isCityworks }),
      });
      const data = await res.json();
      if (!data.success) { showToast(data.error || 'Could not start trial', 'error'); setUpgrading(null); return; }
      // If they picked a non-Professional plan, upgrade immediately (trial, no charge)
      const defaultPriceId = billing?.isCityworks ? billing?.availablePlans?.monthly?.professional : billing?.availablePlans?.monthly?.professional;
      if (priceId && priceId !== defaultPriceId) {
        await apiFetch('/api/billing/upgrade', { method: 'POST', body: JSON.stringify({ cityId: user.cityId, priceId }) });
      }
      showToast(`${planName} trial started — 30 days free, no credit card required`);
      // Reload billing status
      const statusRes = await apiFetch(`/api/billing/status/${user.cityId}`);
      const statusData = await statusRes.json();
      if (statusData.success) setBilling(statusData);
    } catch { showToast('Could not start trial — try again', 'error'); }
    setUpgrading(null);
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading billing info...</div>;

  // Determine state
  const status = billing?.status;
  const isNoTrial = !billing || status === 'no_trial';
  const isTrialing = status === 'trialing';
  const isActive = status === 'active';
  const isPastDue = status === 'past_due';
  const isPaused = status === 'paused';
  const isCanceled = status === 'canceled';
  const isManual = billing?.manuallyActivated;
  const hasSubscription = !!billing?.subscriptionId;

  const daysLeft = billing?.trialDaysRemaining || 0;
  const trialEnd = billing?.trialEndsAt ? new Date(billing.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';
  const licenseExpiry = billing?.paidThrough ? new Date(billing.paidThrough) : null;
  const licenseExpired = licenseExpiry && licenseExpiry < new Date();
  const licenseDaysLeft = licenseExpiry ? Math.ceil((licenseExpiry - new Date()) / 86400000) : null;

  const plans = billing?.availablePlans || {};
  const planList = billing?.isCityworks ? [
    { key: 'starter', name: 'Starter', monthlyPrice: '$250', annualPrice: '$2,500', monthlyPriceId: plans.monthly?.starter, annualPriceId: plans.annual?.starter, subs: 'Up to 100/mo' },
    { key: 'growth', name: 'Growth', monthlyPrice: '$500', annualPrice: '$5,000', monthlyPriceId: plans.monthly?.growth, annualPriceId: plans.annual?.growth, subs: 'Up to 250/mo' },
    { key: 'professional', name: 'Professional', monthlyPrice: '$750', annualPrice: '$7,500', monthlyPriceId: plans.monthly?.professional, annualPriceId: plans.annual?.professional, subs: 'Up to 500/mo', recommended: true },
  ] : [
    { key: 'starter', name: 'Starter', monthlyPrice: '$200', annualPrice: '$2,000', monthlyPriceId: plans.monthly?.starter, annualPriceId: plans.annual?.starter, subs: 'Up to 100/mo' },
    { key: 'growth', name: 'Growth', monthlyPrice: '$400', annualPrice: '$4,000', monthlyPriceId: plans.monthly?.growth, annualPriceId: plans.annual?.growth, subs: 'Up to 250/mo' },
    { key: 'professional', name: 'Professional', monthlyPrice: '$600', annualPrice: '$6,000', monthlyPriceId: plans.monthly?.professional, annualPriceId: plans.annual?.professional, subs: 'Up to 500/mo', recommended: true },
  ];

  // Answers add-on delta — the incremental cost above the base plan (same delta for CW and non-CW)
  const answersAddOnPricing = {
    starter:      { monthly: '+$150/mo', annual: '+$1,500/yr' },
    growth:       { monthly: '+$200/mo', annual: '+$2,000/yr' },
    professional: { monthly: '+$300/mo', annual: '+$3,000/yr' },
  };

  // Combined totals (base plan + Answers delta) — the actual Stripe Answers price IDs represent these totals
  const answersWithBaseTotals = billing?.isCityworks
    ? { starter: { monthly: '$400/mo', annual: '$4,000/yr' }, growth: { monthly: '$700/mo', annual: '$7,000/yr' }, professional: { monthly: '$1,050/mo', annual: '$10,500/yr' } }
    : { starter: { monthly: '$350/mo', annual: '$3,500/yr' }, growth: { monthly: '$600/mo', annual: '$6,000/yr' }, professional: { monthly: '$900/mo', annual: '$9,000/yr' } };

  const currentManualTier = isManual && billing?.planName
    ? (billing.planName.toLowerCase().includes('starter') ? 'starter'
      : billing.planName.toLowerCase().includes('growth') ? 'growth'
      : 'professional')
    : null;

  // Status banner config
  const statusBanner = isManual
    ? { emoji: licenseExpired ? '⚠️' : '✅', label: licenseExpired ? 'License Expired' : `${billing?.planName ? billing.planName + ' — ' : ''}Licensed through ${licenseExpiry ? licenseExpiry.toLocaleDateString('en-US',{month:'long',day:'numeric',year:'numeric'}) : 'No expiry set'}`, sub: licenseExpired ? 'Contact Centricity GIS to renew your license.' : licenseDaysLeft !== null ? `${licenseDaysLeft} days remaining on your current license.` : '', color: licenseExpired ? 'var(--danger)' : licenseDaysLeft <= 30 ? '#F59E0B' : 'var(--success)' }
    : isTrialing ? { emoji: '🎉', label: `Free Trial — ${daysLeft} day${daysLeft !== 1 ? 's' : ''} remaining`, sub: `Trial ends ${trialEnd}. No charge until then — no credit card required.`, color: daysLeft <= 3 ? 'var(--danger)' : daysLeft <= 7 ? '#F59E0B' : 'var(--success)' }
    : isActive ? { emoji: '✅', label: `Active — ${billing?.planName || 'CivicBot Plan'}`, sub: 'Your subscription is active.', color: 'var(--success)' }
    : isPastDue ? { emoji: '⚠️', label: 'Payment Past Due', sub: 'Your payment method failed. Update it to avoid service interruption.', color: '#F59E0B' }
    : isPaused ? { emoji: '🔴', label: 'Subscription Paused', sub: 'Your trial ended without a payment method. Add one to resume.', color: 'var(--danger)' }
    : isCanceled ? { emoji: '✕', label: 'Subscription Canceled', sub: 'Your subscription has been canceled.', color: 'var(--danger)' }
    : { emoji: '💳', label: `Start your ${billing?.trialDays || 30}-day free trial`, sub: 'No credit card required. Full access during trial.', color: 'var(--accent-light)' };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Billing</div>
        <div className="page-subtitle">Manage your CivicBot subscription</div>
      </div>

      {/* Status banner */}
      <div className="billing-card" style={{marginBottom:20}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:6}}>
              <span style={{fontSize:26}}>{statusBanner.emoji}</span>
              <span style={{fontSize:18,fontWeight:700,color:statusBanner.color}}>{statusBanner.label}</span>
            </div>
            {statusBanner.sub && <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.5,maxWidth:480}}>{statusBanner.sub}</div>}
            {isTrialing && daysLeft <= 7 && (
              <div style={{marginTop:10,background:'#F59E0B15',border:'1px solid #F59E0B40',borderRadius:8,padding:'8px 14px',fontSize:12,color:'#F59E0B',fontWeight:600}}>
                ⏰ Add a payment method now to avoid interruption — use "Activate Plan" below.
              </div>
            )}
          </div>
          {hasSubscription && (
            <button className="save-btn" onClick={openPortal} style={{flexShrink:0}}>
              {isTrialing ? '🚀 Activate Plan (Selected Below)' : '💳 Manage Billing & Payment'}
            </button>
          )}
        </div>
      </div>

      {/* Plan grid — always visible */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header">
          <div className="card-title">{isManual ? 'Your Licensed Plan' : isNoTrial ? 'Choose Your Plan' : 'Available Plans'}</div>
          <span style={{fontSize:12, color:'var(--text-muted)'}}>
            {billing?.isCityworks ? 'Cityworks/Trimble Unity integration' : 'Email delivery'}
          </span>
        </div>
        <div className="card-body">
          {isNoTrial && !isManual && (
            <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:10,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#1E40AF',lineHeight:1.6}}>
              🎉 <strong>Start your {billing?.trialDays || 30}-day free trial</strong> — pick the plan that fits your city. No credit card required. You can switch plans anytime during your trial.
            </div>
          )}
          {isManual ? (
            <div style={{fontSize:12,color:'var(--text-muted)',textAlign:'center',marginBottom:20,padding:'8px 0',borderBottom:'1px solid var(--border)'}}>
              Your plan is managed by Centricity GIS via invoice or purchase order. Contact us to make changes.
            </div>
          ) : (
            <div style={{display:'flex',justifyContent:'center',marginBottom:20}}>
              <div style={{display:'flex',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:3,gap:3}}>
                <button onClick={() => setBillingInterval('monthly')} style={{padding:'6px 20px',borderRadius:6,border:'none',background: billingInterval === 'monthly' ? 'var(--accent)' : 'transparent',color: billingInterval === 'monthly' ? '#fff' : 'var(--text-muted)',fontWeight:600,fontSize:13,cursor:'pointer',transition:'all 0.15s'}}>Monthly</button>
                <button onClick={() => setBillingInterval('annual')} style={{padding:'6px 20px',borderRadius:6,border:'none',background: billingInterval === 'annual' ? 'var(--accent)' : 'transparent',color: billingInterval === 'annual' ? '#fff' : 'var(--text-muted)',fontWeight:600,fontSize:13,cursor:'pointer',transition:'all 0.15s'}}>
                  Annual <span style={{fontSize:10,background:'#10B981',color:'#fff',padding:'1px 5px',borderRadius:4,marginLeft:4}}>Save 17%</span>
                </button>
              </div>
            </div>
          )}
          <div className="billing-plans-grid">
            {planList.map(plan => {
              const priceId = billingInterval === 'annual' ? plan.annualPriceId : plan.monthlyPriceId;
              const isCurrent = billing?.currentPriceId === priceId || (isManual && currentManualTier === plan.key);
              const isProcessing = upgrading === priceId;
              return (
                <div key={plan.key} className={`billing-plan-option ${isCurrent ? 'current' : ''}`} style={{position:'relative'}}>
                  {plan.recommended && !isCurrent && <div style={{position:'absolute',top:-10,left:'50%',transform:'translateX(-50%)',background:'var(--accent)',color:'#fff',fontSize:10,fontWeight:700,padding:'2px 10px',borderRadius:10,whiteSpace:'nowrap'}}>MOST POPULAR</div>}
                  <div className="billing-plan-price">
                    {isManual ? (billing?.billingPeriod === 'monthly' ? plan.monthlyPrice : plan.annualPrice) : (billingInterval === 'annual' ? plan.annualPrice : plan.monthlyPrice)}
                    <span style={{fontSize:12,fontWeight:400,color:'var(--text-muted)'}}>{(isManual ? billing?.billingPeriod === 'monthly' : billingInterval === 'monthly') ? '/mo' : '/yr'}</span>
                  </div>
                  <div className="billing-plan-name-sm">{plan.name}</div>
                  <div className="billing-plan-subs">{plan.subs}</div>
                  {!isManual && billingInterval === 'annual' && <div style={{fontSize:10,color:'#10B981',marginTop:2}}>Billed annually — save 17%</div>}
                  <div style={{marginTop:8,paddingTop:8,borderTop:'1px solid var(--border)',fontSize:11,color:'var(--text-muted)'}}>
                    🤖 Add Answers Q&A: <span style={{color:'var(--accent-light)',fontWeight:600}}>{answersAddOnPricing[plan.key]?.[isManual ? (billing?.billingPeriod || 'annual') : billingInterval]}</span>
                    <span style={{fontSize:10,color:'var(--text-muted)',display:'block',marginTop:2}}>({answersWithBaseTotals[plan.key]?.[isManual ? (billing?.billingPeriod || 'annual') : billingInterval]} with Answers)</span>
                  </div>
                  <div style={{marginTop:12}}>
                    {isCurrent ? (
                      <div style={{fontSize:11,color:'var(--accent-light)',fontWeight:600,textAlign:'center'}}>{isManual ? '✓ Licensed' : '✓ Current Plan'}</div>
                    ) : isManual ? (
                      <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'center'}}>—</div>
                    ) : (isNoTrial && !isManual) ? (
                      <button
                        onClick={() => handleStartTrial(priceId, plan.name + (billingInterval === 'annual' ? ' Annual' : ' Monthly'))}
                        disabled={isProcessing || !!upgrading}
                        style={{width:'100%',padding:'8px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor: isProcessing || upgrading ? 'not-allowed' : 'pointer',opacity: isProcessing || upgrading ? 0.6 : 1}}
                      >
                        {isProcessing ? 'Starting trial...' : 'Start Free Trial'}
                      </button>
                    ) : hasSubscription ? (
                      <button
                        onClick={() => handleUpgrade(priceId, plan.name + (billingInterval === 'annual' ? ' Annual' : ' Monthly'))}
                        disabled={isProcessing || (isPaused || isCanceled)}
                        style={{width:'100%',padding:'8px 0',background:'var(--accent)',color:'#fff',border:'none',borderRadius:6,fontSize:12,fontWeight:600,cursor: isProcessing ? 'not-allowed' : 'pointer',opacity: isProcessing ? 0.6 : 1}}
                      >
                        {isProcessing ? 'Switching...' : 'Select Plan'}
                      </button>
                    ) : (
                      <div style={{fontSize:11,color:'var(--text-muted)',textAlign:'center'}}>—</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{marginTop:16, fontSize:12, color:'var(--text-muted)', textAlign:'center'}}>
            Annual plans lock in for 12 months. Monthly plans can be changed anytime. Enterprise plans available — <a href="mailto:info@centricitygis.com" style={{color:'var(--accent-light)'}}>contact us</a>.
          </div>
        </div>
      </div>

      {/* CivicBot Answers add-on card */}
      {(() => {
        // Already licensed for Answers
        if (billing?.isAnswersPlan) {
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header"><div className="card-title">🤖 CivicBot Answers</div></div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{fontSize:28}}>✅</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:15}}>CivicBot Answers is active</div>
                    <div style={{fontSize:13,color:'var(--text-muted)',marginTop:2}}>Residents can ask natural language questions and get grounded answers from your city website.</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Manually licensed base plan — show add-on with "contact us" CTA
        if (isManual) {
          const tier = currentManualTier || 'professional';
          const period = billing?.billingPeriod || 'annual';
          const addOnLabel = answersAddOnPricing[tier]?.[period] || '';
          const totalLabel = answersWithBaseTotals[tier]?.[period] || '';
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:220}}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>Supercharge your widget with resident Q&A</div>
                    <ul style={{margin:'0 0 12px 0',paddingLeft:18,fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
                      <li>AI answers grounded in your city website content</li>
                      <li>Every answer includes source links residents can verify</li>
                      <li>Unanswered questions logged so you can fill gaps</li>
                      <li>Automatic weekly knowledge base refresh</li>
                    </ul>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,minWidth:170,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>Add-on cost</div>
                    <div style={{fontSize:22,fontWeight:700,color:'var(--accent-light)'}}>{addOnLabel}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)',paddingTop:6,width:'100%'}}>{tier.charAt(0).toUpperCase()+tier.slice(1)} plan + Answers = <strong style={{color:'var(--text)'}}>{totalLabel}</strong></div>
                    <a
                      href="mailto:info@centricitygis.com?subject=CivicBot%20Answers%20Add-on&body=Please%20add%20CivicBot%20Answers%20to%20our%20account."
                      style={{display:'block',textAlign:'center',padding:'9px 16px',background:'var(--accent)',borderRadius:8,color:'white',fontSize:12,fontWeight:600,textDecoration:'none',width:'100%',boxSizing:'border-box',marginTop:4}}
                    >
                      Contact Us to Add Answers
                    </a>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Active subscription or active trial with plan selected — show matched tier with self-serve button
        if ((isActive || isPastDue || isTrialing) && billing?.currentPriceId) {
          const rank = billing.allPrices?.[billing.currentPriceId]?.rank;
          const tier = rank === 1 ? 'starter' : rank === 2 ? 'growth' : 'professional';
          const period = billing.billingPeriod || 'annual';
          const addOnLabel = answersAddOnPricing[tier]?.[period] || '';
          const totalLabel = answersWithBaseTotals[tier]?.[period] || '';
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:20,flexWrap:'wrap'}}>
                  <div style={{flex:1,minWidth:220}}>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:6}}>Supercharge your widget with resident Q&A</div>
                    <ul style={{margin:'0 0 12px 0',paddingLeft:18,fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
                      <li>AI answers grounded in your city website content</li>
                      <li>Every answer includes source links residents can verify</li>
                      <li>Unanswered questions logged so you can fill gaps</li>
                      <li>Automatic weekly knowledge base refresh</li>
                    </ul>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:6,minWidth:170,textAlign:'center'}}>
                    <div style={{fontSize:11,color:'var(--text-muted)'}}>Add-on cost</div>
                    <div style={{fontSize:22,fontWeight:700,color:'var(--accent-light)'}}>{addOnLabel}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',borderTop:'1px solid var(--border)',paddingTop:6,width:'100%'}}>{tier.charAt(0).toUpperCase()+tier.slice(1)} plan + Answers = <strong style={{color:'var(--text)'}}>{totalLabel}</strong></div>
                    <button
                      onClick={handleAddAnswers}
                      disabled={addingAnswers}
                      style={{padding:'9px 24px',background:'var(--accent)',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',cursor: addingAnswers ? 'not-allowed' : 'pointer',opacity: addingAnswers ? 0.7 : 1,width:'100%',marginTop:4}}
                    >
                      {addingAnswers ? 'Processing...' : 'Add Answers'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        // Trial or no subscription yet — teaser only, no pricing shown until plan is confirmed
        if (isTrialing || isNoTrial) {
          return (
            <div className="card" style={{marginTop:20}}>
              <div className="card-header">
                <div className="card-title">🤖 CivicBot Answers Add-on</div>
                <span style={{fontSize:12,color:'var(--text-muted)'}}>AI-powered resident Q&A with source citations</span>
              </div>
              <div className="card-body">
                <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                  <div style={{fontSize:40,flexShrink:0}}>🤖</div>
                  <div>
                    <div style={{fontWeight:600,fontSize:14,marginBottom:4}}>CivicBot Answers is available as an add-on</div>
                    <div style={{fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>Add AI-powered Q&A grounded in your city website. Pricing matches your plan tier — confirm your subscription above to see your Answers add-on price and activate it.</div>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* Contact / Support card */}
      <div className="card" style={{marginTop:20}}>
        <div className="card-header">
          <div className="card-title">Need Help?</div>
        </div>
        <div className="card-body" style={{display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:16}}>
          <div style={{fontSize:13,color:'var(--text-dim)',lineHeight:1.7}}>
            Questions about your plan, billing, or getting set up? Our team is here to help.
          </div>
          <div style={{display:'flex',gap:12,flexWrap:'wrap'}}>
            <a
              href="mailto:info@centricitygis.com"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',background:'var(--card-bg)',border:'1px solid var(--border)',borderRadius:8,color:'var(--text)',fontSize:13,fontWeight:500,textDecoration:'none'}}
            >
              ✉️ info@centricitygis.com
            </a>
            <a
              href="https://civicbot.centricitygis.com"
              target="_blank"
              rel="noreferrer"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',background:'var(--accent)',border:'none',borderRadius:8,color:'#fff',fontSize:13,fontWeight:500,textDecoration:'none'}}
            >
              🌐 civicbot.centricitygis.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Email SR Types Editor ────────────────────────────────────────────────────
function EmailSRTypesEditor({ showToast }) {
  const DEFAULTS = ['Pothole Report', 'Street Light Outage', 'Abandoned Vehicle'];
  const [srTypes, setSrTypes] = useState(() => {
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    const saved = localStorage.getItem(`civicbot_sr_types_${cityId}`);
    return saved ? JSON.parse(saved) : DEFAULTS;
  });
  const [saved, setSaved] = useState(false);

  const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;

  function addType() { setSrTypes(prev => [...prev, '']); setSaved(false); }
  function removeType(i) { setSrTypes(prev => prev.filter((_, idx) => idx !== i)); setSaved(false); }
  function updateType(i, val) { setSrTypes(prev => prev.map((t, idx) => idx === i ? val : t)); setSaved(false); }

  async function handleSave() {
    const cleaned = srTypes.map(t => t.trim()).filter(Boolean);
    if (cleaned.length === 0) { showToast('Add at least one SR type', 'error'); return; }

    // Save to localStorage
    localStorage.setItem(`civicbot_sr_types_${cityId}`, JSON.stringify(cleaned));

    // Save to backend — load existing config first to avoid wiping other fields
    try {
      const cfgRes = await apiFetch(`/api/config/${cityId}`).then(r => r.json());
      const existing = cfgRes.success ? cfgRes.config : {};
      const saveRes = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId,
          cityName: existing.cityName,
          greeting: existing.greeting,
          primaryColor: existing.primaryColor,
          state: existing.state,
          wkid: existing.wkid,
          emailDelivery: true,
          notificationEmails: existing.notificationEmails,
          geofenceEnabled: existing.geofenceEnabled,
          geofenceMessage: existing.geofenceMessage,
          selectedTemplates: existing.selectedTemplates || [],
          aliases: existing.aliases || {},
          srTypes: cleaned,
        }),
      });
      const saveData = await saveRes.json();
      if (saveData.success) {
        setSrTypes(cleaned);
        setSaved(true);
        showToast(`Saved ${cleaned.length} service request types`);
        setTimeout(() => setSaved(false), 3000);
      } else {
        showToast(saveData.error || 'Failed to save', 'error');
      }
    } catch { showToast('Failed to save — check your connection', 'error'); }
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Service Request Types</div>
        <div className="page-subtitle">Define the issue types residents can report through CivicBot</div>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-label">SR Types Configured</div>
          <div className="stat-value" style={{color:'var(--accent-light)'}}>{srTypes.filter(t=>t.trim()).length}</div>
          <div className="stat-sub">Residents can choose from these</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Delivery Method</div>
          <div className="stat-value" style={{fontSize:18, marginTop:10}}>📧</div>
          <div className="stat-sub">Email delivery</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Widget Status</div>
          <div className="stat-value" style={{fontSize:18, marginTop:10, color:'var(--success)'}}>✅</div>
          <div className="stat-sub">Ready to embed</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Configure SR Types</div>
          <span style={{fontSize:12, color:'var(--text-muted)'}}>These appear as options in your CivicBot chat widget</span>
        </div>
        <div className="card-body">
          <div style={{display:'flex', flexDirection:'column', gap:8, marginBottom:16}}>
            {srTypes.map((type, i) => (
              <div key={i} style={{display:'flex', gap:8, alignItems:'center'}}>
                <div style={{width:28, height:28, borderRadius:6, background:'var(--surface2)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, color:'var(--text-muted)', flexShrink:0}}>{i + 1}</div>
                <input
                  className="setting-input"
                  style={{flex:1}}
                  value={type}
                  onChange={e => updateType(i, e.target.value)}
                  placeholder="e.g. Pothole Report"
                  autoFocus={i === srTypes.length - 1 && type === ''}
                />
                <button
                  onClick={() => removeType(i)}
                  style={{width:32, height:32, background:'none', border:'1px solid var(--border-light)', borderRadius:6, color:'var(--text-muted)', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.15s'}}
                  onMouseOver={e => { e.target.style.borderColor='var(--danger)'; e.target.style.color='var(--danger)'; }}
                  onMouseOut={e => { e.target.style.borderColor='var(--border-light)'; e.target.style.color='var(--text-muted)'; }}
                  title="Remove"
                >✕</button>
              </div>
            ))}
          </div>

          <button
            onClick={addType}
            style={{display:'flex', alignItems:'center', gap:8, padding:'9px 16px', background:'none', border:'1px dashed var(--border-light)', borderRadius:8, color:'var(--accent-light)', fontSize:13, fontFamily:'inherit', cursor:'pointer', transition:'all 0.15s', width:'100%', justifyContent:'center'}}
            onMouseOver={e => e.currentTarget.style.borderColor='var(--accent)'}
            onMouseOut={e => e.currentTarget.style.borderColor='var(--border-light)'}
          >
            <span style={{fontSize:16, fontWeight:600}}>+</span> Add Service Request Type
          </button>

          <div style={{marginTop:16, background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)', lineHeight:1.6}}>
            💡 <strong style={{color:'var(--text-dim)'}}>Tip:</strong> Be specific but simple. "Pothole Report" works better than "Road Surface Damage Issue". Residents will see exactly these names in the chatbot.
          </div>
        </div>
      </div>

      {!saved && (
        <div className="save-bar">
          <span className="save-bar-text"><strong>{srTypes.filter(t=>t.trim()).length} SR types</strong> configured</span>
          <button className="save-btn" onClick={handleSave}>Save SR Types</button>
        </div>
      )}
    </div>
  );
}

// ─── Templates Page ────────────────────────────────────────────────────────────
function TemplatesPage({ showToast }) {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [aliases, setAliases] = useState({});
  const [editingAlias, setEditingAlias] = useState(null);
  const [aliasInput, setAliasInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('ALL');
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEmailOnly, setIsEmailOnly] = useState(false);
  const [needsCWSetup, setNeedsCWSetup] = useState(false);

  useEffect(() => {
    const savedAliases = JSON.parse(localStorage.getItem('civicbot_aliases') || '{}');
    setAliases(savedAliases);

    // Load city config first to determine type
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { setLoading(false); return; }

    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(async cfg => {
        if (!cfg.success) { setLoading(false); return; }
        const config = cfg.config;

        if (config.emailDelivery) {
          // Email-only city — use their configured SR types, no Cityworks
          const srTypes = JSON.parse(localStorage.getItem('civicbot_sr_types') || '[]');
          setIsEmailOnly(true);
          setLoading(false);
          return;
        }

        // Cityworks city — check if THIS city has CW credentials configured
        const hasCWCredentials = config.cwBaseUrl && config.cwUser;

        if (!hasCWCredentials) {
          setNeedsCWSetup(true);
          setLoading(false);
          return;
        }

        // Load Cityworks templates
        apiFetch('/api/sr-templates?cityId=' + cityId)
          .then(r => r.json())
          .then(data => {
            if (data.success) {
              setTemplates(data.templates);
              const savedIds = JSON.parse(localStorage.getItem('civicbot_selected_templates') || '[]');
              if (savedIds.length > 0) setSelected(new Set(savedIds));
              else setSelected(new Set(data.templates.filter(t => t.ForPublicSite).map(t => t.ProblemSid)));
            }
          })
          .catch(() => showToast('Failed to load templates', 'error'))
          .finally(() => setLoading(false));
      })
      .catch(() => { setLoading(false); showToast('Failed to load city config', 'error'); });
  }, [showToast]);

  const categories = ['ALL', ...new Set(templates.map(t => t.ProbCategory))];
  const filtered = templates.filter(t => {
    const displayName = aliases[t.ProblemSid] || t.Description;
    const matchSearch = displayName.toLowerCase().includes(search.toLowerCase()) || t.ProblemCode.toLowerCase().includes(search.toLowerCase());
    const matchCat = catFilter === 'ALL' || t.ProbCategory === catFilter;
    return matchSearch && matchCat;
  });

  function toggle(sid) { setSelected(prev => { const next = new Set(prev); next.has(sid) ? next.delete(sid) : next.add(sid); return next; }); setSaved(false); }
  function selectAll() { setSelected(new Set(filtered.map(t => t.ProblemSid))); setSaved(false); }
  function selectNone() { setSelected(prev => { const next = new Set(prev); filtered.forEach(t => next.delete(t.ProblemSid)); return next; }); setSaved(false); }
  function startEditAlias(sid) { setEditingAlias(sid); setAliasInput(aliases[sid] || ''); }
  function saveAlias(sid) {
    const trimmed = aliasInput.trim();
    setAliases(prev => { const next = { ...prev }; if (trimmed) next[sid] = trimmed; else delete next[sid]; return next; });
    setEditingAlias(null); setSaved(false);
  }
  function clearAlias(sid) { setAliases(prev => { const next = { ...prev }; delete next[sid]; return next; }); setSaved(false); }
  async function handleSave() {
    localStorage.setItem('civicbot_selected_templates', JSON.stringify([...selected]));
    localStorage.setItem('civicbot_aliases', JSON.stringify(aliases));
    // Also persist to backend so the widget picks them up
    try {
      const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
      if (cityId) {
        const cfgRes = await apiFetch('/api/config/' + cityId).then(r => r.json());
        const existing = (cfgRes && cfgRes.config) || {};
        await apiFetch('/api/config', {
          method: 'POST',
          body: JSON.stringify({
            cityId: cityId,
            cityName: existing.cityName || '',
            slug: existing.slug || '',
            greeting: existing.greeting || '',
            primaryColor: existing.primaryColor || '#3B82F6',
            logoUrl: existing.logoUrl || '',
            cwBaseUrl: existing.cwBaseUrl || '',
            cwUser: existing.cwUser || '',
            cwPassword: '',
            state: existing.state || '',
            wkid: existing.wkid || 4326,
            zoneName: existing.zoneName || '',
            selectedTemplates: [...selected],
            aliases: aliases,
            geofenceEnabled: existing.geofenceEnabled || false,
            geofenceMessage: existing.geofenceMessage || '',
            geofenceLat: existing.geofenceLat || null,
            geofenceLng: existing.geofenceLng || null,
            geofenceCityName: existing.geofenceCityName || '',
            emailDelivery: existing.emailDelivery || false,
            notificationEmails: existing.notificationEmails || '',
            srTypes: existing.srTypes || [],
            assignedSalesRep: existing.assignedSalesRep || '',
            sendSubmitterConfirmation: existing.sendSubmitterConfirmation !== false,
          }),
        });
      }
    } catch (e) {
      console.error('Backend save failed:', e);
      showToast('Local saved, but backend sync failed', 'error');
      return;
    }
    setSaved(true);
    showToast('Saved ' + selected.size + ' SR types with ' + Object.keys(aliases).length + ' aliases');
  }

  const embedCode = generateEmbedCode(templates.filter(t => selected.has(t.ProblemSid)));
  function copyEmbed() { navigator.clipboard.writeText(embedCode); setCopied(true); setTimeout(() => setCopied(false), 2000); showToast('Embed code copied!'); }

  if (loading) return <div className="loading"><div className="spinner" />Loading SR templates...</div>;

  if (isEmailOnly) {
    return <EmailSRTypesEditor showToast={showToast} />;
  }

  if (needsCWSetup) {
    return (
      <div>
        <div className="page-header">
          <div className="page-title">Service Request Templates</div>
          <div className="page-subtitle">Configure your Cityworks connection to load SR types</div>
        </div>
        <div className="card">
          <div className="card-body" style={{textAlign:'center', padding:48}}>
            <div style={{fontSize:32, marginBottom:16}}>🔗</div>
            <div style={{fontSize:16, fontWeight:600, marginBottom:8}}>Cityworks Connection Required</div>
            <div style={{fontSize:13, color:'var(--text-muted)', lineHeight:1.6, marginBottom:24}}>
              To load your SR types, first configure your Cityworks/Trimble Unity connection in Settings.<br/>
              You'll need your <strong>Cityworks Base URL</strong> and <strong>API credentials</strong>.
            </div>
            <button className="save-btn" onClick={() => window.dispatchEvent(new CustomEvent('navigate', {detail:'settings'}))}>
              Go to Settings →
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Service Request Templates</div>
        <div className="page-subtitle">Select SR types and set friendly display names for the chatbot</div>
      </div>
      <div className="stats-row">
        <div className="stat-card"><div className="stat-label">Total Templates</div><div className="stat-value">{templates.length}</div><div className="stat-sub">In Trimble Unity</div></div>
        <div className="stat-card"><div className="stat-label">Enabled for CivicBot</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{selected.size}</div><div className="stat-sub">Residents can submit these</div></div>
        <div className="stat-card"><div className="stat-label">Custom Aliases</div><div className="stat-value" style={{color:'#8B5CF6'}}>{Object.keys(aliases).length}</div><div className="stat-sub">Renamed for residents</div></div>
      </div>
      <div className="card">
        <div className="card-header">
          <div className="card-title">Configure SR Types</div>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>{selected.size} of {templates.length} selected</span>
            <button onClick={async () => { setLoading(true); try { const cityId2 = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
const data = await apiFetch('/api/sr-templates' + (cityId2 ? '?cityId=' + cityId2 : '')).then(r=>r.json()); if (data.success) { setTemplates(data.templates); showToast('SR types synced from Cityworks (' + data.templates.length + ' loaded)'); } else { showToast(data.error || 'Sync failed', 'error'); } } catch { showToast('Sync failed', 'error'); } setLoading(false); }} style={{padding:'6px 14px',background:'var(--surface2)',border:'1px solid var(--border-light)',borderRadius:6,color:'var(--accent-light)',fontSize:12,fontFamily:'inherit',cursor:'pointer',fontWeight:600}}>🔄 Sync from Cityworks</button>
            <span style={{fontSize:11,color:'var(--text-muted)'}}>Only pulls templates marked "For Public Website" in Cityworks</span>
          </div>
        </div>
        <div className="card-body">
          <div className="filter-row">
            <input className="search-input" placeholder="Search by name or code..." value={search} onChange={e => setSearch(e.target.value)} />
            {categories.map(cat => <button key={cat} className={`filter-btn ${catFilter===cat?'active':''}`} onClick={() => setCatFilter(cat)}>{cat==='ALL'?'All':getCat(cat).label}</button>)}
          </div>
          <div className="select-all-row">
            <span>{filtered.length} templates shown</span>
            <div style={{display:'flex',gap:12}}>
              <button className="select-all-btn" onClick={selectAll}>Select All</button>
              <span style={{color:'var(--border-light)'}}>|</span>
              <button className="select-all-btn" onClick={selectNone}>Deselect All</button>
            </div>
          </div>
          <div className="sr-list">
            {filtered.map(t => {
              const cat = getCat(t.ProbCategory);
              const isSelected = selected.has(t.ProblemSid);
              const hasAlias = !!aliases[t.ProblemSid];
              const isEditing = editingAlias === t.ProblemSid;
              return (
                <div key={t.ProblemSid} className={`sr-item ${isSelected?'selected':''}`} onClick={e => { if (!e.target.closest('.alias-area')) toggle(t.ProblemSid); }}>
                  <div className="sr-checkbox">{isSelected && <span className="sr-checkbox-check">✓</span>}</div>
                  <div className="sr-info" style={{flex:1}}>
                    <div className="sr-name">{t.Description}</div>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginTop:4,flexWrap:'wrap'}}>
                      <div className="sr-code">{t.ProblemCode}</div>
                      <div className="alias-area" onClick={e => e.stopPropagation()}>
                        {isEditing ? (
                          <div style={{display:'flex',gap:4,alignItems:'center'}}>
                            <input autoFocus value={aliasInput} onChange={e => setAliasInput(e.target.value)} onKeyDown={e => { if(e.key==='Enter') saveAlias(t.ProblemSid); if(e.key==='Escape') setEditingAlias(null); }} placeholder={t.Description} style={{width:180,padding:'3px 8px',background:'var(--surface3)',border:'1px solid var(--accent)',borderRadius:5,color:'var(--text)',fontSize:11,fontFamily:'inherit',outline:'none'}} />
                            <button onClick={() => saveAlias(t.ProblemSid)} style={{background:'var(--accent)',border:'none',borderRadius:4,color:'white',padding:'3px 8px',cursor:'pointer',fontSize:11}}>✓</button>
                            <button onClick={() => setEditingAlias(null)} style={{background:'var(--surface3)',border:'1px solid var(--border-light)',borderRadius:4,color:'var(--text-muted)',padding:'3px 6px',cursor:'pointer',fontSize:11}}>✕</button>
                          </div>
                        ) : hasAlias ? (
                          <div style={{display:'flex',alignItems:'center',gap:4}}>
                            <span style={{fontSize:11,color:'#8B5CF6',background:'#8B5CF615',padding:'2px 7px',borderRadius:4,border:'1px solid #8B5CF630'}}>→ {aliases[t.ProblemSid]}</span>
                            <button onClick={() => startEditAlias(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:11,padding:'0 2px'}}>✏️</button>
                            <button onClick={() => clearAlias(t.ProblemSid)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer',fontSize:10,padding:'0 2px'}}>✕</button>
                          </div>
                        ) : (
                          <button onClick={() => startEditAlias(t.ProblemSid)} style={{background:'none',border:'1px dashed var(--border-light)',borderRadius:4,color:'var(--text-muted)',fontSize:10,padding:'2px 8px',cursor:'pointer',fontFamily:'inherit'}}>+ alias</button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{display:'flex',gap:4,alignItems:'center',flexShrink:0}}>
                    <span className="sr-badge" style={{background:cat.bg,color:cat.color}}>{cat.label}</span>
                    {t.ForPublicSite && <span className="sr-public-badge">Public</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="card" style={{marginTop:20}}>
        <div className="card-header"><div className="card-title">Your Embed Code</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Paste this on your city website</span></div>
        <div className="card-body">
          <div className="embed-box">
            <pre className="embed-code">{embedCode}</pre>
            <button className={`copy-btn ${copied?'copied':''}`} onClick={copyEmbed}>{copied?'✓ Copied':'Copy'}</button>
          </div>
        </div>
      </div>
      {!saved && (
        <div className="save-bar">
          <span className="save-bar-text"><strong>{selected.size} SR types</strong> · <strong>{Object.keys(aliases).length} aliases</strong></span>
          <button className="save-btn" onClick={handleSave}>Save Configuration</button>
        </div>
      )}
    </div>
  );
}

// ─── Embed Page ────────────────────────────────────────────────────────────────
function EmbedPage({ user }) {
  const [copied, setCopied] = useState(false);
  const [copiedStandalone, setCopiedStandalone] = useState(false);
  const cityId = user?.cityId || 'your-city-id';
  // Read color from backend config (most up to date)
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  useEffect(() => {
    if (!cityId || cityId === 'your-city-id') return;
    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(data => { if (data.success && data.config?.primaryColor) setPrimaryColor(data.config.primaryColor); })
      .catch(() => {
        const saved = JSON.parse(localStorage.getItem('civicbot_settings') || '{}');
        if (saved.primaryColor) setPrimaryColor(saved.primaryColor);
      });
  }, [cityId]);
  const standaloneUrl = `https://civicbot.centricitygis.com/submit.html?city=${cityId}`;
  const code = `<script src="https://civicbot.centricitygis.com/widget.js"\n  data-city-id="${cityId}"\n  data-color="${primaryColor}"\n  async>\n</script>`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(standaloneUrl)}&color=ffffff&bgcolor=111827`;

  function copy() { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }
  function copyStandalone() { navigator.clipboard.writeText(standaloneUrl); setCopiedStandalone(true); setTimeout(() => setCopiedStandalone(false), 2000); }

  return (
    <div>
      <div className="page-header"><div className="page-title">Embed Code</div><div className="page-subtitle">Add CivicBot to your city website — or share a direct link with residents</div></div>

      {/* Website Widget */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">🌐 Website Widget</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Embed on your city website</span></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>Paste this script tag just before the closing <code style={{background:'var(--surface3)',padding:'2px 6px',borderRadius:4,fontSize:12}}>&lt;/body&gt;</code> tag on your city website. The chat bubble will appear in the bottom-right corner.</p>
          <div className="embed-box">
            <pre className="embed-code">{code}</pre>
            <button className={`copy-btn ${copied?'copied':''}`} onClick={copy}>{copied?'✓ Copied':'Copy'}</button>
          </div>
          <div className="widget-preview">
            <div className="widget-preview-title">Live Preview</div>
            <div className="widget-chat"><div className="widget-chat-name">CivicBot</div>Hi! I'm CivicBot. I can help you submit a service request to the city. What issue would you like to report today?</div>
            <div className="widget-bubble" style={{background:`linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`}}>{user?.cityLogo ? <img src={user.cityLogo} alt="logo" style={{width:28,height:28,borderRadius:6,objectFit:"contain"}} /> : "🏙️"}</div>
          </div>
        </div>
      </div>

      {/* Standalone Page + QR Code */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📱 Standalone Resident Page</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Share directly — no website needed</span></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>
            Share this link via email, social media, or print the QR code for city signage, flyers, and bulletins. Residents go straight to your CivicBot without needing your city website.
          </p>
          <div className="embed-box" style={{marginBottom:20}}>
            <pre className="embed-code">{standaloneUrl}</pre>
            <button className={`copy-btn ${copiedStandalone?'copied':''}`} onClick={copyStandalone}>{copiedStandalone?'✓ Copied':'Copy'}</button>
          </div>

          <div style={{display:'flex',gap:24,alignItems:'flex-start',flexWrap:'wrap'}}>
            {/* QR Code */}
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:12}}>QR Code</div>
              <div style={{background:'#111827',border:'1px solid var(--border)',borderRadius:12,padding:16,display:'inline-block'}}>
                <img src={qrUrl} alt="QR Code" width={160} height={160} style={{display:'block',borderRadius:4}} />
              </div>
              <div style={{marginTop:10,display:'flex',gap:8,justifyContent:'center'}}>
                <a href={qrUrl} download={`civicbot-qr-${cityId}.png`} style={{padding:'6px 14px',background:'var(--surface2)',border:'1px solid var(--border-light)',borderRadius:6,color:'var(--text-dim)',fontSize:11,textDecoration:'none',transition:'all 0.15s'}}
                  onMouseOver={e=>e.target.style.borderColor='var(--accent)'}
                  onMouseOut={e=>e.target.style.borderColor='var(--border-light)'}>
                  ⬇ Download PNG
                </a>
              </div>
            </div>

            {/* Usage tips */}
            <div style={{flex:1,minWidth:200}}>
              <div style={{fontSize:12,fontWeight:600,color:'var(--text-dim)',textTransform:'uppercase',letterSpacing:'0.5px',marginBottom:12}}>Ways to share</div>
              {[
                ['📧', 'Email blast', 'Include the link or QR code in your city newsletter'],
                ['🏛️', 'City signage', 'Print and post QR codes at city hall, public works buildings'],
                ['📱', 'Social media', "Post the link on your city's Facebook, Nextdoor, or X page"],
                ['📰', 'City website', 'Add the link or use the embed code on your website'],
                ['🖨️', 'Flyers', 'Include on utility bills, door hangers, or community flyers'],
              ].map(([icon, title, desc]) => (
                <div key={title} style={{display:'flex',gap:10,marginBottom:10,alignItems:'flex-start'}}>
                  <span style={{fontSize:16,flexShrink:0}}>{icon}</span>
                  <div>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--text)'}}>{title}</div>
                    <div style={{fontSize:11,color:'var(--text-muted)',lineHeight:1.4}}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Test your widget */}
      <div className="card">
        <div className="card-header"><div className="card-title">🧪 Test Your Widget</div></div>
        <div className="card-body">
          <p style={{fontSize:13,color:'var(--text-muted)',marginBottom:16,lineHeight:1.6}}>Open your standalone page to test the full submission flow before sharing with residents.</p>
          <a href={standaloneUrl} target="_blank" rel="noreferrer" style={{display:'inline-block',padding:'10px 20px',background:'var(--accent)',borderRadius:8,color:'white',fontSize:13,fontWeight:600,textDecoration:'none',transition:'background 0.2s'}}
            onMouseOver={e=>e.currentTarget.style.background='#2563EB'}
            onMouseOut={e=>e.currentTarget.style.background='var(--accent)'}>
            Open My CivicBot Page →
          </a>
        </div>
      </div>
    </div>
  );
}


// ─── Geofence Validator Component ────────────────────────────────────────────
function GeofenceValidator({ cityName, state, onChange, onVerified }) {
  const [status, setStatus] = useState(null); // null | 'checking' | 'valid' | 'invalid' | 'error'
  const [validatedName, setValidatedName] = useState('');
  const [matchedName, setMatchedName] = useState('');

  async function verify() {
    if (!cityName.trim() || !state) return;
    setStatus('checking');
    try {
      const res = await apiFetch(`/api/verify-place?name=${encodeURIComponent(cityName.trim())}&state=${encodeURIComponent(state)}`);
      const data = await res.json();
      if (data.found) {
        setMatchedName(data.matchedName);
        setStatus('valid');
        setValidatedName(cityName.trim());
        // Pass verified coordinates up so they can be saved with settings
        if (onVerified) onVerified({ lat: data.lat, lng: data.lng, matchedName: data.matchedName });
      } else {
        setStatus('invalid');
        setMatchedName('');
      }
    } catch (err) {
      console.error('Geofence verify error:', err);
      setStatus('error');
    }
  }



  const inputBorder = status === 'valid' ? '#10B981' : status === 'invalid' ? '#EF4444' : 'var(--border-light)';

  return (
    <div className="setting-field">
      <label className="setting-label">Census Place Name</label>
      <div style={{display:'flex',gap:8,alignItems:'center'}}>
        <input
          className="setting-input"
          value={cityName}
          onChange={e => { onChange(e.target.value); setStatus(null); }}
          placeholder="e.g. Highland"
          style={{flex:1, borderColor: inputBorder}}
        />
        <button
          onClick={verify}
          disabled={!cityName.trim() || status === 'checking'}
          style={{padding:'10px 16px',background:'var(--accent)',border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',cursor:'pointer',whiteSpace:'nowrap',opacity:(!cityName.trim() || status === 'checking') ? 0.5 : 1}}
        >
          {status === 'checking' ? '⏳ Checking...' : '🔍 Verify'}
        </button>
      </div>

      {/* Status messages */}
      {status === 'valid' && (
        <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981',marginTop:8}}>
          ✅ <strong>Verified!</strong> Found "{matchedName}" in the Census database for {state}.
          {matchedName !== validatedName && <span style={{color:'#94A3B8'}}> (You entered "{validatedName}" — consider updating to match exactly: "{matchedName}")</span>}
        </div>
      )}
      {status === 'invalid' && (
        <div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#EF4444',marginTop:8}}>
          ❌ <strong>Not found.</strong> "{cityName}" was not found in the Census database for {state}. Check spelling or try without "City of" / "Town of" prefix. Visit <a href="https://www.census.gov/quickfacts/" target="_blank" style={{color:'#F87171'}}>census.gov/quickfacts</a> to find the exact name.
        </div>
      )}
      {status === 'error' && (
        <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B',marginTop:8}}>
          ⚠️ Could not reach Census database. Check your internet connection and try again.
        </div>
      )}
      {status === null && (
        <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,lineHeight:1.5}}>
          Enter your city name and click Verify. We'll automatically look it up and confirm it exists in the Census database.
        </div>
      )}
    </div>
  );
}

// ─── Settings Page ─────────────────────────────────────────────────────────────
function SettingsPage({ showToast }) {
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('civicbot_settings');
    // Always use defaults — API load will populate from DB
    return {
      cityName: '', greeting: 'Hi! I can help you submit a service request. What issue would you like to report?',
      primaryColor: '#3B82F6', logoUrl: '',
      cwBaseUrl: '', cwUser: '', cwPassword: '',
      state: '', wkid: 4326, zoneName: '',
      geofenceEnabled: false, geofenceMessage: 'This address appears to be outside city limits. Please verify the address or contact the city directly for assistance.', geofenceCityName: '',
      emailDelivery: false, notificationEmails: '',
      sendSubmitterConfirmation: true, notifyUnansweredQA: false, isAnswersPlan: false,
      adminEmail: '',
    };
  });
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Load city config from backend on mount
  useEffect(() => {
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { setSettingsLoading(false); return; }
    apiFetch(`/api/config/${cityId}`)
      .then(r => r.json())
      .then(data => {
        if (data.success && data.config) {
          const cfg = data.config;
          const defaults = {
            greeting: 'Hi! I can help you submit a service request. What issue would you like to report?',
            primaryColor: '#3B82F6',
          };
          setSettings(prev => ({
            ...prev,
            cityName: cfg.cityName || prev.cityName,
            greeting: cfg.greeting || defaults.greeting,
            primaryColor: cfg.primaryColor || defaults.primaryColor,
            logoUrl: cfg.logoUrl || '',
            emailDelivery: !!cfg.emailDelivery,
            notificationEmails: cfg.notificationEmails || prev.notificationEmails,
            geofenceEnabled: !!cfg.geofenceEnabled,
            geofenceMessage: cfg.geofenceMessage || prev.geofenceMessage,
            geofenceLat: cfg.geofenceLat || null,
            geofenceLng: cfg.geofenceLng || null,
            geofenceCityName: cfg.geofenceCityName || '',
            state: cfg.state || prev.state,
            wkid: cfg.wkid || prev.wkid,
            zoneName: cfg.zoneName || prev.zoneName,
            cwBaseUrl: cfg.cwBaseUrl || prev.cwBaseUrl || '',
            cwUser: cfg.cwUser || prev.cwUser || '',
            sendSubmitterConfirmation: cfg.send_submitter_confirmation !== undefined ? !!cfg.send_submitter_confirmation : (cfg.sendSubmitterConfirmation !== undefined ? !!cfg.sendSubmitterConfirmation : true),
            notifyUnansweredQA: !!cfg.notifyUnansweredQA,
            isAnswersPlan: !!cfg.isAnswersPlan,
            adminEmail: cfg.adminEmail || '',
            geofenceLat: cfg.geofenceLat || null,
            geofenceLng: cfg.geofenceLng || null,
            geofenceCityName: cfg.geofenceCityName || prev.geofenceCityName || '',
            geofenceEnabled: cfg.geofenceEnabled !== undefined ? !!cfg.geofenceEnabled : prev.geofenceEnabled,
            geofenceMessage: cfg.geofenceMessage || prev.geofenceMessage,
          }));
        }
      })
      .catch(() => {})
      .finally(() => setSettingsLoading(false));
  }, []);
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);

  const US_STATES = [
    ['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],
    ['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['FL','Florida'],['GA','Georgia'],
    ['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],
    ['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],
    ['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],
    ['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],
    ['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],
    ['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],
    ['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],
    ['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming'],
  ];

  useEffect(() => {
    if (!settings.state) { setZones([]); return; }
    setLoadingZones(true);
    apiFetch(`/api/state-plane-zones?state=${settings.state}`)
      .then(r => r.json())
      .then(data => { if (data.success) setZones(data.zones); })
      .finally(() => setLoadingZones(false));
  }, [settings.state]);

  async function save() {
    // Validate State Plane Zone is set for Cityworks clients
    if (!settings.emailDelivery && (!settings.wkid || settings.wkid === 4326)) {
      showToast('⚠️ State Plane Zone is required for Cityworks clients — please select your zone before saving.', 'error');
      return;
    }
    localStorage.setItem('civicbot_settings', JSON.stringify(settings));
    const cityId = JSON.parse(localStorage.getItem('civicbot_user') || '{}').cityId;
    if (!cityId) { showToast('Settings saved locally'); return; }

    try {
      // Load existing config to preserve fields we don't manage on Settings page
      const cfgRes = await apiFetch(`/api/config/${cityId}`).then(r => r.json());
      const existing = cfgRes.success ? cfgRes.config : {};

      const res = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId,
          cityName: settings.cityName || existing.cityName,
          greeting: settings.greeting,
          primaryColor: settings.primaryColor,
          logoUrl: settings.logoUrl,
          state: settings.state,
          wkid: settings.wkid,
          zoneName: settings.zoneName,
          cwBaseUrl: settings.cwBaseUrl,
          cwUser: settings.cwUser,
          cwPassword: settings.cwPassword,
          emailDelivery: settings.emailDelivery,
          notificationEmails: settings.notificationEmails,
          geofenceEnabled: settings.geofenceEnabled,
          geofenceMessage: settings.geofenceMessage,
          geofenceCityName: settings.geofenceCityName || '',
          geofenceLat: settings.geofenceLat || null,
          geofenceLng: settings.geofenceLng || null,
          selectedTemplates: existing.selectedTemplates || [],
          aliases: existing.aliases || {},
          srTypes: existing.srTypes || [],
          sendSubmitterConfirmation: settings.sendSubmitterConfirmation,
          notifyUnansweredQA: settings.notifyUnansweredQA,
          adminEmail: settings.adminEmail,
        }),
      });
      const data = await res.json();
      if (data.success) showToast('Settings saved');
      else showToast(data.error || 'Failed to save settings', 'error');
    } catch (err) {
      showToast('Could not reach server — saved locally only', 'error');
    }
  }

  return (
    <div>
      <div className="page-header"><div className="page-title">Settings</div><div className="page-subtitle">Configure your CivicBot instance</div></div>

      {/* Branding */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Branding</div></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field"><label className="setting-label">City Name</label><input className="setting-input" value={settings.cityName} onChange={e => setSettings({...settings,cityName:e.target.value})} placeholder="City of Springfield" /></div>
            <div className="setting-field"><label className="setting-label">Admin Contact Email</label><input className="setting-input" type="email" value={settings.adminEmail} onChange={e => setSettings({...settings,adminEmail:e.target.value})} placeholder="admin@yourcity.gov" /><span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Used for unanswered Q&A notifications and account communications.</span></div>
            <div className="setting-field setting-full">
              <label className="setting-label">City Logo <span style={{color:"var(--text-muted)",fontWeight:400}}>(recommended: 200x200 PNG/SVG, under 2MB)</span></label>
              <div style={{display:"flex",alignItems:"center",gap:16,marginTop:4}}>
                {settings.logoUrl
                  ? <img src={settings.logoUrl} alt="City logo" className="logo-preview" />
                  : <div style={{width:80,height:80,borderRadius:12,background:"var(--surface2)",border:"1px solid var(--border)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28}}>🏙️</div>
                }
                <div style={{flex:1}}>
                  <label className="logo-upload-box" style={{display:"block"}}>
                    <input type="file" accept="image/*" style={{display:"none"}} onChange={async e => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { showToast("Logo must be under 2MB", "error"); return; }
                      const cityId = JSON.parse(localStorage.getItem("civicbot_user")||"{}").cityId;
                      const formData = new FormData();
                      formData.append("logo", file);
                      if (cityId) formData.append("cityId", cityId);
                      try {
                        const token = localStorage.getItem("civicbot_token");
                        const res = await fetch(API_BASE + "/api/upload-logo", { method: "POST", headers: { Authorization: "Bearer " + token }, body: formData });
                        const data = await res.json();
                        if (data.success) {
                          setSettings(s => ({...s, logoUrl: data.logoUrl}));
                          const u = JSON.parse(localStorage.getItem("civicbot_user")||"{}");
                          u.cityLogo = data.logoUrl;
                          localStorage.setItem("civicbot_user", JSON.stringify(u));
                          showToast("Logo uploaded successfully!");
                        } else { showToast(data.error || "Upload failed", "error"); }
                      } catch { showToast("Upload failed", "error"); }
                    }} />
                    <div style={{fontSize:13,color:"var(--accent-light)",fontWeight:600}}>📁 Click to upload logo</div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>JPG, PNG, SVG or WebP - Max 2MB</div>
                  </label>
                  {settings.logoUrl && <button onClick={() => setSettings(s=>({...s,logoUrl:""}))} style={{marginTop:8,padding:"4px 10px",background:"none",border:"1px solid var(--border-light)",borderRadius:6,color:"var(--text-muted)",fontSize:11,cursor:"pointer",fontFamily:"inherit"}}>Remove logo</button>}
                </div>
              </div>
            </div>
            <div className="setting-field"><label className="setting-label">Primary Color</label><div style={{display:'flex',gap:8,alignItems:'center'}}><input className="setting-input" value={settings.primaryColor} onChange={e => setSettings({...settings,primaryColor:e.target.value})} style={{flex:1}} /><input type="color" value={settings.primaryColor} onChange={e => setSettings({...settings,primaryColor:e.target.value})} style={{width:36,height:36,borderRadius:8,border:'1px solid var(--border-light)',background:'none',cursor:'pointer',padding:2,flexShrink:0}} title="Pick a color" /></div></div>
            <div className="setting-field setting-full"><label className="setting-label">Welcome Message</label><input className="setting-input" value={settings.greeting} onChange={e => setSettings({...settings,greeting:e.target.value})} /></div>
          </div>
        </div>
      </div>

      {/* Location */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Location & Coordinate System</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Required for accurate map placement in Cityworks</span></div>
        <div className="card-body">
          <div className="settings-grid">
            <div className="setting-field">
              <label className="setting-label">State</label>
              <select className="setting-input" value={settings.state} onChange={e => setSettings(s => ({...s,state:e.target.value,wkid:4326,zoneName:''}))} style={{cursor:'pointer'}}>
                <option value="">Select state...</option>
                {US_STATES.map(([code,name]) => <option key={code} value={code}>{name}</option>)}
              </select>
            </div>
            <div className="setting-field">
              <label className="setting-label" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <span style={{display:'flex',alignItems:'center'}}>State Plane Zone {loadingZones && <span style={{color:'var(--text-muted)',fontSize:10}}> Loading...</span>}<Tooltip text="A State Plane Zone (WKID) tells CivicBot how to convert GPS coordinates into your Cityworks map projection. Without the correct zone, SR locations will appear in the wrong place on the map. Ask your GIS department if unsure — they will know immediately." /></span>
                <a href="https://www.ngs.noaa.gov/SPCS/index.shtml" target="_blank" rel="noreferrer" style={{fontSize:10,color:'var(--accent-light)',fontWeight:400,textDecoration:'none'}}>Help finding your zone</a>
              </label>
              <select className="setting-input" value={settings.wkid} onChange={e => { const zone=zones.find(z=>z.wkid===parseInt(e.target.value)); setSettings(s=>({...s,wkid:parseInt(e.target.value),zoneName:zone?.name||''})); }} disabled={zones.length===0} style={{cursor:zones.length>0?'pointer':'not-allowed'}}>
                <option value={4326}>{zones.length===0?'Select state first...':'Select zone...'}</option>
                {zones.map(z => <option key={z.wkid} value={z.wkid}>{z.name} (WKID: {z.wkid})</option>)}
              </select>
            </div>
            {settings.wkid && settings.wkid !== 4326 ? (
              <div className="setting-field setting-full"><div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#10B981',display:'flex',alignItems:'center',gap:8}}>✓ Using <strong>{settings.zoneName}</strong> — WKID {settings.wkid}</div></div>
            ) : !settings.emailDelivery && (
              <div className="setting-field setting-full"><div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:13,color:'#EF4444',display:'flex',alignItems:'center',gap:8}}>⚠️ <strong>State Plane Zone is required</strong> for Cityworks clients. Without it, map pins will be placed incorrectly. Please select your state and zone above.</div></div>
            )}
            <div className="setting-field setting-full">
              <label className="setting-label">Or enter WKID manually <span style={{color:'var(--text-muted)',fontWeight:400}}>(advanced)</span></label>
              <input className="setting-input" type="number" value={settings.wkid} onChange={e => setSettings({...settings,wkid:parseInt(e.target.value),zoneName:'Custom'})} placeholder="e.g. 102100" />
            </div>
          </div>
        </div>
      </div>

      {/* Geofencing */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title" style={{display:'flex',alignItems:'center'}}>🗺️ City Limits Geofencing<Tooltip text="Geofencing prevents residents from submitting requests for addresses outside your city. It uses U.S. Census Bureau boundary data. You must click Verify City Location after enabling — without that step, geofencing will not activate." /></div><span style={{fontSize:12,color:'var(--text-muted)'}}>Optional — powered by US Census TIGERweb</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info"><div className="toggle-row-label">Restrict submissions to city limits</div><div className="toggle-row-sub">Verify submitted addresses fall within your city boundary. Addresses outside city limits will be rejected with a friendly message.</div></div>
            <button className={`toggle-switch ${settings.geofenceEnabled?'on':''}`} onClick={() => setSettings(s=>({...s,geofenceEnabled:!s.geofenceEnabled}))}><div className="toggle-thumb" /></button>
          </div>
          {settings.geofenceEnabled && !settings.state && <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B'}}>⚠️ Please select your State above — required for city boundary lookup.</div>}
          {settings.geofenceEnabled && settings.state && (
            <GeofenceValidator
              cityName={settings.geofenceCityName || ''}
              state={settings.state}
              onChange={val => setSettings(s => ({...s, geofenceCityName: val}))}
              onVerified={({lat, lng, matchedName}) => setSettings(s => ({...s, geofenceLat: lat, geofenceLng: lng, geofenceCityName: matchedName}))}
            />
          )}
          {settings.geofenceEnabled && settings.state && !settings.geofenceCityName && <div style={{background:'#F59E0B15',border:'1px solid #F59E0B30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#F59E0B'}}>⚠️ Enter your Census Place Name above to activate geofencing.</div>}
          {settings.geofenceEnabled && <div className="setting-field"><label className="setting-label">Out-of-bounds message</label><input className="setting-input" value={settings.geofenceMessage} onChange={e => setSettings({...settings,geofenceMessage:e.target.value})} /></div>}
        </div></div>
      </div>

      {/* Email Delivery */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">✉️ Email Delivery</div><span style={{fontSize:12,color:'var(--text-muted)'}}>For organizations without Cityworks or Trimble Unity</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info"><div className="toggle-row-label">Enable email delivery</div><div className="toggle-row-sub">Service requests are packaged and emailed to your team. A CB-YYYY-XXXXX confirmation number is generated for each submission.</div></div>
            <button className={`toggle-switch ${settings.emailDelivery?'on':''}`} onClick={() => setSettings(s=>({...s,emailDelivery:!s.emailDelivery}))}><div className="toggle-thumb" /></button>
          </div>
          {settings.emailDelivery && <>
            <div className="setting-field">
              <label className="setting-label" style={{display:'flex',alignItems:'center'}}>Notification Email Addresses<Tooltip text="All email addresses listed here will receive a formatted notification for every service request submitted. Separate multiple addresses with commas. Consider using a shared inbox (e.g. 311@yourcity.gov) rather than individual staff emails." /></label>
              <input className="setting-input" value={settings.notificationEmails} onChange={e => setSettings({...settings,notificationEmails:e.target.value})} placeholder="dispatcher@city.gov, publicworks@city.gov" />
              <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Separate multiple addresses with commas.</span>
            </div>

            <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>✓ Email delivery active — submissions sent to <strong>{settings.notificationEmails||'your configured addresses'}</strong></div>
          </>}
        </div></div>
      </div>

      {/* Submitter Confirmation Email */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📧 Submitter Confirmation Email</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Send a receipt to residents who provide their email</span></div>
        <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div className="toggle-row">
            <div className="toggle-row-info">
              <div className="toggle-row-label">Send confirmation email to submitter</div>
              <div className="toggle-row-sub">When a resident includes their email address, they will automatically receive a confirmation with their request details and confirmation number.</div>
            </div>
            <button className={`toggle-switch ${settings.sendSubmitterConfirmation?'on':''}`} onClick={() => setSettings(s=>({...s,sendSubmitterConfirmation:!s.sendSubmitterConfirmation}))}><div className="toggle-thumb" /></button>
          </div>
          {settings.sendSubmitterConfirmation && (
            <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>
              Residents who provide their email will receive a confirmation after submitting.
            </div>
          )}
        </div></div>
      </div>

      {/* CivicBot Answers — Unanswered Q notification (only for Answers cities) */}
      {settings.isAnswersPlan && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><div className="card-title">🤖 CivicBot Answers Notifications</div><span style={{fontSize:12,color:'var(--text-muted)'}}>Get notified when a resident question can't be answered</span></div>
          <div className="card-body"><div style={{display:'flex',flexDirection:'column',gap:16}}>
            <div className="toggle-row">
              <div className="toggle-row-info">
                <div className="toggle-row-label">Notify me when a question can't be answered</div>
                <div className="toggle-row-sub">When CivicBot can't find an answer in your knowledge base, you'll receive an email with the resident's question so you can decide whether to add that information to your website.</div>
              </div>
              <button className={`toggle-switch ${settings.notifyUnansweredQA?'on':''}`} onClick={() => setSettings(s=>({...s,notifyUnansweredQA:!s.notifyUnansweredQA}))}><div className="toggle-thumb" /></button>
            </div>
            {settings.notifyUnansweredQA && (
              <div style={{background:'#10B98115',border:'1px solid #10B98130',borderRadius:8,padding:'10px 14px',fontSize:12,color:'#10B981'}}>
                Unanswered questions will be emailed to your city admin account.
              </div>
            )}
          </div></div>
        </div>
      )}

      {/* Delivery Method Toggle */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">Delivery Method</div><span style={{fontSize:12,color:'var(--text-muted)'}}>How service requests are delivered to your team</span></div>
        <div className="card-body">
          <div style={{display:'flex',gap:10}}>
            <button
              onClick={() => setSettings(s=>({...s,emailDelivery:true}))}
              style={{flex:1,padding:'14px 16px',borderRadius:10,border:`2px solid ${settings.emailDelivery?'var(--accent)':'var(--border-light)'}`,background:settings.emailDelivery?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',transition:'all 0.15s',textAlign:'left'}}>
              <div style={{fontSize:20,marginBottom:6}}>📧</div>
              <div style={{fontSize:13,fontWeight:600,color:settings.emailDelivery?'var(--accent-light)':'var(--text)'}}>Email Delivery</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>SRs emailed to your team. No Cityworks required.</div>
            </button>
            <button
              onClick={() => setSettings(s=>({...s,emailDelivery:false}))}
              style={{flex:1,padding:'14px 16px',borderRadius:10,border:`2px solid ${!settings.emailDelivery?'var(--accent)':'var(--border-light)'}`,background:!settings.emailDelivery?'var(--accent-dim)':'var(--surface2)',cursor:'pointer',transition:'all 0.15s',textAlign:'left'}}>
              <div style={{fontSize:20,marginBottom:6}}>🔗</div>
              <div style={{fontSize:13,fontWeight:600,color:!settings.emailDelivery?'var(--accent-light)':'var(--text)'}}>Cityworks / Trimble Unity</div>
              <div style={{fontSize:11,color:'var(--text-muted)',marginTop:3,lineHeight:1.4}}>SRs submitted directly into your Cityworks instance.</div>
            </button>
          </div>
        </div>
      </div>

      {/* Trimble Unity - only show when Cityworks selected */}
      {!settings.emailDelivery && (
        <div className="card" style={{marginBottom:20}}>
          <div className="card-header"><div className="card-title">Trimble Unity / Cityworks Connection</div></div>
          <div className="card-body">
            <div className="settings-grid">
              <div className="setting-field setting-full"><label className="setting-label" style={{display:'flex',alignItems:'center'}}>Cityworks Base URL<Tooltip text="Enter the root URL of your Cityworks or Trimble Unity instance. Example: https://partners.maintain.unity.trimble.com/YOUR_ORG — no trailing slash. For on-premise Cityworks, this is typically your internal server URL." /></label><input className="setting-input" value={settings.cwBaseUrl} onChange={e => setSettings({...settings,cwBaseUrl:e.target.value})} placeholder="https://partners.maintain.unity.trimble.com/YOUR_ORG" /></div>
              <div className="setting-field"><label className="setting-label">API Username</label><input className="setting-input" value={settings.cwUser} onChange={e => setSettings({...settings,cwUser:e.target.value})} placeholder="APIUser" /></div>
              <div className="setting-field"><label className="setting-label">API Password</label><input className="setting-input" type="password" value={settings.cwPassword||''} onChange={e => setSettings({...settings,cwPassword:e.target.value})} placeholder="••••••••••" /></div>
            </div>
            <CWVerifyButton cwBaseUrl={settings.cwBaseUrl} cwUser={settings.cwUser} cwPassword={settings.cwPassword} />
            <div style={{marginTop:16,background:'#3B82F615',border:'1px solid #3B82F630',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-dim)'}}>
              💡 After saving your Cityworks credentials, go to <strong>SR Templates</strong> to load and configure your SR types.
            </div>
          </div>
        </div>
      )}

      <button className="save-btn" style={{padding:'11px 24px'}} onClick={save}>Save Settings</button>
    </div>
  );
}


// CW Verify Button Component
function CWVerifyButton({ cwBaseUrl, cwUser, cwPassword }) {
  const [status, setStatus] = useState(null);
  const [message, setMessage] = useState('');

  async function verify() {
    if (!cwBaseUrl || !cwUser || !cwPassword) {
      setStatus('error'); setMessage('Please fill in Base URL, username, and password first'); return;
    }
    setStatus('checking'); setMessage('');
    try {
      const res = await apiFetch('/api/verify-cw', {
        method: 'POST',
        body: JSON.stringify({ cwBaseUrl, cwUser, cwPassword }),
      });
      const data = await res.json();
      if (data.success) { setStatus('success'); setMessage(data.message || 'Connection successful!'); }
      else { setStatus('error'); setMessage(data.error || 'Connection failed'); }
    } catch { setStatus('error'); setMessage('Could not reach server'); }
  }

  const colors = { success: { bg:'#10B98115', border:'#10B98130', text:'#10B981' }, error: { bg:'#EF444415', border:'#EF444430', text:'#EF4444' } };
  const c = colors[status] || {};

  return (
    <div style={{marginTop:12, display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
      <button onClick={verify} disabled={status==='checking'} style={{padding:'8px 16px', background:'var(--surface2)', border:'1px solid var(--border-light)', borderRadius:8, color:'var(--text-muted)', fontSize:12, fontFamily:'inherit', cursor:'pointer', fontWeight:600, transition:'all 0.15s', opacity:status==='checking'?0.6:1}}>
        {status==='checking' ? 'Testing connection...' : 'Test Connection'}
      </button>
      {status && status !== 'checking' && (
        <div style={{background:c.bg, border:'1px solid '+c.border, borderRadius:8, padding:'7px 14px', fontSize:12, color:c.text, fontWeight:500}}>
          {message}
        </div>
      )}
    </div>
  );
}

// ─── Cities Page (Super Admin) ────────────────────────────────────────────────
function CitiesPage({ user, showToast, setImpersonating }) {
  const [deleteCity, setDeleteCity] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  async function handleDeleteCity() {
    if (deleteConfirmText !== deleteCity.name) return;
    setDeleting(true);
    try {
      const res = await apiFetch('/api/admin/cities/' + deleteCity.id, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        showToast('City permanently deleted');
        setDeleteCity(null);
        setDeleteConfirmText('');
        loadCities();
      } else {
        showToast(data.error || 'Delete failed', 'error');
      }
    } catch { showToast('Delete failed', 'error'); }
    setDeleting(false);
  }
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editCity, setEditCity] = useState(null);
  const [viewCity, setViewCity] = useState(null);

  const [activateCity, setActivateCity] = useState(null);
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }

  function sortIndicator(field) {
    if (sortField !== field) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  function getExpiry(city) {
    if (city.manually_activated === 1 && city.paid_through) return new Date(city.paid_through);
    if (city.paid_through) return new Date(city.paid_through);
    if (city.trial_ends_at) return new Date(city.trial_ends_at);
    return null;
  }


  function loadCities() {
    setLoading(true);
    apiFetch('/api/admin/cities')
      .then(r => r.json())
      .then(data => { if (data.success) setCities(data.cities); })
      .catch(() => showToast('Failed to load cities', 'error'))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadCities(); }, []);

  const filtered = cities.filter(c =>
    !search ||
    (c.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.admin_email || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.state || '').toLowerCase().includes(search.toLowerCase())
  ).sort((a, b) => {
    let av, bv;
    if (sortField === 'name') { av = a.name || ''; bv = b.name || ''; }
    else if (sortField === 'status') { av = a.billing_status || ''; bv = b.billing_status || ''; }
    else if (sortField === 'plan') { av = a.plan_name || ''; bv = b.plan_name || ''; }
    else if (sortField === 'submissions') { av = a.submission_count || 0; bv = b.submission_count || 0; }
    else if (sortField === 'expiry') {
      av = a.manually_activated ? (a.paid_through || '') : (a.trial_ends_at || '');
      bv = b.manually_activated ? (b.paid_through || '') : (b.trial_ends_at || '');
    }
    else if (sortField === 'created_at') { av = a.created_at || ''; bv = b.created_at || ''; }
    else if (sortField === 'referral') { av = a.referral_source || ''; bv = b.referral_source || ''; }
    else if (sortField === 'salesrep') { av = a.assigned_sales_rep || ''; bv = b.assigned_sales_rep || ''; }
    else { av = ''; bv = ''; }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  function statusBadge(city) {
    const s = city.billing_status || 'no_trial';
    if (city.manually_activated === 1) {
      const expired = city.paid_through && new Date(city.paid_through) < new Date();
      return <span className={`status-badge ${expired ? 'past_due' : 'active'}`}>{expired ? '⚠️ Manual Expired' : '✅ Manual'}</span>;
    }
    const labels = { trialing: '🎉 Trial', active: '✅ Active', past_due: '⚠️ Past Due', paused: '⏸ Paused', no_trial: '— No Trial' };
    return <span className={`status-badge ${s}`}>{labels[s] || s}</span>;
  }

  function trialDays(city) {
    if (!city.trial_ends_at) return '—';
    const days = Math.max(0, Math.ceil((new Date(city.trial_ends_at) - new Date()) / 86400000));
    return days > 0 ? `${days}d left` : 'Expired';
  }

  async function openStripePortal(city) {
    if (!city.stripe_customer_id) { showToast('No billing account for this city', 'error'); return; }
    try {
      const res = await apiFetch('/api/billing/portal', { method: 'POST', body: JSON.stringify({ cityId: city.id, returnUrl: window.location.href }) });
      const data = await res.json();
      if (data.success) window.open(data.url, '_blank');
      else showToast('Could not open billing portal', 'error');
    } catch { showToast('Could not open billing portal', 'error'); }
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading cities...</div>;

  async function handleImpersonate(city) {
    if (!window.confirm('Enter admin panel as ' + city.name + '? Your session will be saved and restored on exit.')) return;
    try {
      const res = await apiFetch('/api/admin/impersonate/' + city.id, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      // Save super admin session, swap in city token
      localStorage.setItem('civicbot_super_token', localStorage.getItem('civicbot_token'));
      localStorage.setItem('civicbot_super_user', localStorage.getItem('civicbot_user'));
      localStorage.setItem('civicbot_token', data.token);
      localStorage.setItem('civicbot_impersonating', city.name);
      localStorage.setItem('civicbot_user', JSON.stringify({ cityId: city.id, email: data.email, role: 'city_admin', cityName: city.name, isAnswersPlan: city.is_answers_plan === 1 }));
      setImpersonating(city.name);
      window.location.reload();
    } catch (err) {
      showToast('Impersonation failed: ' + err.message);
    }
  }

  function exportCitiesCSV() {
    const headers = ['City','Slug','State','Plan','Billing Status','Trial Ends','Submissions','Created'];
    const rows = cities.map(c => [
      c.name || '', c.slug || '', c.state || '',
      c.plan_name || '', c.billing_status || '',
      c.trial_ends_at ? new Date(c.trial_ends_at).toLocaleDateString() : '',
      c.submission_count || 0,
      c.created_at ? new Date(c.created_at).toLocaleDateString() : ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g,'""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'civicbot-cities.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const totalCities = cities.length;
  const activeCities = cities.filter(c => c.billing_status === 'trialing' || c.billing_status === 'active').length;
  const totalSubs = cities.reduce((a, c) => a + (c.submission_count || 0), 0);

  return (
    <div>
      <div className="page-header">
        <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="page-title">All Cities</div>
            <div className="page-subtitle">Manage all CivicBot customer accounts</div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <button className="btn btn-secondary" onClick={exportCitiesCSV}>📥 Export CSV</button>
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ Create City</button>
          </div>
        </div>
      </div>

      <div className="stats-row">
        <div className="stat-card"><div className="stat-label">Total Cities</div><div className="stat-value">{totalCities}</div><div className="stat-sub">All accounts</div></div>
        <div className="stat-card"><div className="stat-label">Active / Trial</div><div className="stat-value" style={{color:'var(--success)'}}>{activeCities}</div><div className="stat-sub">Live customers</div></div>
        <div className="stat-card"><div className="stat-label">Total Submissions</div><div className="stat-value" style={{color:'var(--accent-light)'}}>{totalSubs}</div><div className="stat-sub">All time, all cities</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Customer Cities</div>
          <input className="search-input" placeholder="Search by name, email, or state..." value={search} onChange={e => setSearch(e.target.value)} style={{width:280}} />
        </div>
        <div style={{overflowX:'auto'}}>
          {filtered.length === 0 ? (
            <div className="empty">No cities yet. Click "Create City" to onboard your first customer.</div>
          ) : (
            <table className="cities-table">
              <thead>
                <tr>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('name')}>City{sortIndicator('name')}</th>
                  <th>Admin Email</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('plan')}>Plan{sortIndicator('plan')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('expiry')}>Expiry{sortIndicator('expiry')}</th>
                  <th>Billing</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('submissions')}>Submissions{sortIndicator('submissions')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('referral')}>Referral{sortIndicator('referral')}</th>
                  <th style={{cursor:'pointer',userSelect:'none'}} onClick={() => toggleSort('salesrep')}>Sales Rep{sortIndicator('salesrep')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(city => (
                  <tr key={city.id}>
                    <td>
                      <div style={{fontWeight:600}}>{city.name}</div>
                      <div style={{fontSize:11, color:'var(--text-muted)'}}>{city.state || '—'} · {city.email_delivery ? '📧 Email' : '🔗 Cityworks'}</div>
                    </td>
                    <td style={{fontSize:12, color:'var(--text-dim)'}}>{city.admin_email || '—'}</td>
                    <td style={{fontSize:12}}>{city.plan_name ? city.plan_name.replace('CivicBot ', '') : <span style={{color:'var(--text-muted)'}}>No plan</span>}</td>
                    <td>{statusBadge(city)}</td>
                    <td style={{fontSize:12}}>
                      {(() => {
                        const exp = getExpiry(city);
                        if (!exp) return <span style={{color:'var(--text-muted)'}}>—</span>;
                        const expired = exp < new Date();
                        const days = Math.ceil((exp - new Date()) / 86400000);
                        return <div>
                          <div style={{color: expired ? 'var(--danger)' : days <= 30 ? '#F59E0B' : 'var(--success)', fontWeight:600}}>{expired ? '⚠️ Expired' : days + 'd left'}</div>
                          <div style={{color:'var(--text-muted)',fontSize:11}}>{exp.toLocaleDateString()}</div>
                        </div>;
                      })()}
                    </td>
                    <td style={{fontSize:12}}>
                      {city.manually_activated === 1 ? (
                        <div>
                          <div style={{fontWeight:600, color:'var(--text)'}}>{city.billing_period === 'monthly' ? '📅 Monthly' : '📆 Annual'}</div>
                          <div style={{color: city.paid_through && new Date(city.paid_through) < new Date() ? 'var(--danger)' : 'var(--text-muted)'}}>
                            {city.paid_through ? 'Until ' + new Date(city.paid_through).toLocaleDateString() : '—'}
                          </div>
                        </div>
                      ) : city.stripe_subscription_id ? (
                        <div>
                          <div style={{fontWeight:600, color:'var(--text)'}}>💳 Stripe</div>
                          <div style={{color:'var(--text-muted)'}}>{city.billing_period === 'monthly' ? 'Monthly' : 'Annual'}</div>
                        </div>
                      ) : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                    <td>
                      <div style={{fontSize:13, fontWeight:600}}>{city.submission_count || 0}</div>
                      <div style={{fontSize:11, color:'var(--text-muted)'}}>all time</div>
                    </td>
                    <td style={{fontSize:12}}>
                      {city.referral_source ? (
                        <div>
                          <div style={{color:'var(--text)'}}>{city.referral_source}</div>
                          {city.referral_detail && <div style={{color:'var(--text-muted)',fontSize:11}}>{city.referral_detail}</div>}
                        </div>
                      ) : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                    <td style={{fontSize:12, color:'var(--text)'}}>{city.assigned_sales_rep ? city.assigned_sales_rep : <span style={{color:'var(--text-muted)'}}>—</span>}</td>
                    <td>
                      <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                        <button className="action-btn" onClick={() => setViewCity(city)}>📋 Submissions</button>
                        <button className="action-btn" onClick={() => setEditCity(city)}>✏️ Edit</button>
                        <button className="action-btn" onClick={() => handleImpersonate(city)} style={{background:'var(--accent)',color:'#fff',border:'none'}}>👁 Login As</button>
                        {city.stripe_customer_id && <button className="action-btn" onClick={() => openStripePortal(city)}>💳 Billing</button>}
                        <button className="action-btn" onClick={() => setActivateCity(city)}>{city.manually_activated ? '🔴 Deactivate' : '✅ Activate'}</button>
                        <button className="action-btn" onClick={() => setDeleteCity(city)} style={{background:'var(--danger)',color:'#fff',border:'none'}}>🗑 Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateCityModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadCities(); showToast('City created and welcome email sent!'); }}
          showToast={showToast}
        />
      )}

      {editCity && (
        <EditCityModal
          city={editCity}
          onClose={() => setEditCity(null)}
          onSaved={() => { setEditCity(null); loadCities(); showToast('City updated successfully'); }}
          showToast={showToast}
        />
      )}

      {viewCity && (
        <CitySubmissionsModal
          city={viewCity}
          onClose={() => setViewCity(null)}
        />
      )}
      {activateCity && (
        <ManualActivationModal
          city={activateCity}
          onClose={() => setActivateCity(null)}
          onSaved={() => { setActivateCity(null); loadCities(); showToast(activateCity.manually_activated ? 'City deactivated' : 'City manually activated!'); }}
          showToast={showToast}
        />
      )}

      {deleteCity && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDeleteCity(null)}>
          <div className="modal" style={{maxWidth:460}}>
            <div className="modal-header">
              <div className="modal-title" style={{color:'var(--danger)'}}>🗑 Delete City — Permanent Action</div>
              <button className="modal-close" onClick={() => { setDeleteCity(null); setDeleteConfirmText(''); }}>✕</button>
            </div>
            <div style={{padding:'24px 28px'}}>
              <div style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:8,padding:'12px 16px',marginBottom:20,fontSize:13,color:'#FCA5A5'}}>
                ⚠️ This will permanently delete <strong>{deleteCity.name}</strong> and all associated submissions, settings, and user accounts. This cannot be undone.
              </div>
              <div style={{marginBottom:8,fontSize:13,color:'var(--text-muted)'}}>Type <strong style={{color:'var(--text)'}}>{deleteCity.name}</strong> to confirm:</div>
              <input
                className="setting-input"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder={deleteCity.name}
                autoFocus
                style={{width:'100%',marginBottom:20}}
              />
              <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
                <button className="btn btn-secondary" onClick={() => { setDeleteCity(null); setDeleteConfirmText(''); }}>Cancel</button>
                <button
                  onClick={handleDeleteCity}
                  disabled={deleteConfirmText !== deleteCity.name || deleting}
                  style={{padding:'8px 20px',background: deleteConfirmText === deleteCity.name ? 'var(--danger)' : 'var(--surface2)',color: deleteConfirmText === deleteCity.name ? '#fff' : 'var(--text-muted)',border:'none',borderRadius:6,fontWeight:600,fontSize:13,cursor: deleteConfirmText === deleteCity.name ? 'pointer' : 'not-allowed',transition:'all 0.15s'}}
                >
                  {deleting ? 'Deleting...' : 'Permanently Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Create City Modal ────────────────────────────────────────────────────────
function ManualActivationModal({ city, onClose, onSaved, showToast }) {
  const isActive = city.manually_activated === 1;
  const oneYearFromNow = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    planName: city.plan_name || 'CivicBot Professional',
    isCwPlan: city.is_cw_plan === 1,
    billingPeriod: city.billing_period || 'annual',
    paidThrough: city.paid_through ? city.paid_through.slice(0, 10) : oneYearFromNow,
    notes: city.manual_activation_notes || '',
  });
  const [saving, setSaving] = useState(false);

  async function handleActivate() {
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/activate-city', { method: 'POST', body: JSON.stringify({ cityId: city.id, ...form }) });
      const data = await res.json();
      if (data.success) onSaved();
      else showToast(data.error || 'Failed to activate', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  async function handleDeactivate() {
    if (!window.confirm('Deactivate ' + city.name + '? This will block new submissions.')) return;
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/deactivate-city', { method: 'POST', body: JSON.stringify({ cityId: city.id, notes: form.notes }) });
      const data = await res.json();
      if (data.success) onSaved();
      else showToast(data.error || 'Failed to deactivate', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isActive ? '🔴 Deactivate City' : '✅ Manual Activation'} — {city.name}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {isActive && (
            <div style={{background:'var(--success)15',border:'1px solid var(--success)30',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--success)',marginBottom:16}}>
              ✅ Currently manually activated — paid through {city.paid_through ? new Date(city.paid_through).toLocaleDateString() : 'no expiry set'}
            </div>
          )}
          <div className="setting-field">
            <label className="setting-label">Plan</label>
            <select className="setting-input" value={form.planName} onChange={e => setForm({...form, planName: e.target.value})} style={{cursor:'pointer'}}>
              <option value="CivicBot Starter">Starter</option>
              <option value="CivicBot Growth">Growth</option>
              <option value="CivicBot Professional">Professional</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">Plan Type</label>
            <select className="setting-input" value={form.isCwPlan ? 'cw' : 'email'} onChange={e => setForm({...form, isCwPlan: e.target.value === 'cw'})} style={{cursor:'pointer'}}>
              <option value="email">Email Delivery</option>
              <option value="cw">Cityworks / Trimble Unity</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">Billing Period</label>
            <select className="setting-input" value={form.billingPeriod} onChange={e => {
              const period = e.target.value;
              const days = period === 'monthly' ? 30 : 365;
              const expiry = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
              setForm({...form, billingPeriod: period, paidThrough: expiry});
            }} style={{cursor:'pointer'}}>
              <option value="annual">Annual (+365 days)</option>
              <option value="monthly">Monthly (+30 days)</option>
            </select>
          </div>
          <div className="setting-field">
            <label className="setting-label">License Expiration Date</label>
            <input className="setting-input" type="date" value={form.paidThrough} onChange={e => setForm({...form, paidThrough: e.target.value})} />
            <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4,display:'block'}}>Auto-calculated from billing period. You can override manually if needed.</span>
          </div>
          <div className="setting-field">
            <label className="setting-label">Notes / Memo</label>
            <input className="setting-input" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="e.g. Check #1234 received 4/28/2026" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {isActive && <button className="btn" style={{background:'var(--danger)',color:'white'}} onClick={handleDeactivate} disabled={saving}>{saving ? 'Deactivating...' : '🔴 Deactivate'}</button>}
          <button className="btn btn-primary" onClick={handleActivate} disabled={saving}>{saving ? 'Saving...' : '✅ Activate'}</button>
        </div>
      </div>
    </div>
  );
}

function CreateCityModal({ onClose, onCreated, showToast }) {
  const [form, setForm] = useState({ cityName: '', state: '', email: '', isCityworks: true, notificationEmails: '', referralSource: '', referralDetail: '' });
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (!form.cityName || !form.email) { showToast('City name and email are required', 'error'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/create-city', { method: 'POST', body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) onCreated();
      else showToast(data.error || 'Failed to create city', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Create New City</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="setting-field">
            <label className="setting-label">City Name *</label>
            <input className="setting-input" value={form.cityName} onChange={e => setForm({...form, cityName: e.target.value})} placeholder="City of Springfield" autoFocus />
          </div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">State</label>
              <input className="setting-input" value={form.state} onChange={e => setForm({...form, state: e.target.value})} placeholder="UT" maxLength={2} />
            </div>
            <div className="setting-field">
              <label className="setting-label">Type</label>
              <select className="setting-input" value={form.isCityworks ? 'cw' : 'email'} onChange={e => setForm({...form, isCityworks: e.target.value === 'cw'})} style={{cursor:'pointer'}}>
                <option value="cw">Cityworks / Trimble Unity</option>
                <option value="email">Email Delivery</option>
              </select>
            </div>
          </div>
          <div className="setting-field">
            <label className="setting-label">Admin Email * <span style={{color:'var(--text-muted)', fontWeight:400}}>(welcome email sent here)</span></label>
            <input className="setting-input" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="admin@cityname.gov" />
          </div>
          {!form.isCityworks && (
            <div className="setting-field">
              <label className="setting-label">Notification Emails</label>
              <input className="setting-input" value={form.notificationEmails} onChange={e => setForm({...form, notificationEmails: e.target.value})} placeholder="dispatch@city.gov, pw@city.gov" />
              <span style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>SR emails will be sent here. Separate multiple with commas.</span>
            </div>
          )}
          <div className="setting-field">
            <label className="setting-label">How did they hear about CivicBot? <span style={{color:'var(--text-muted)',fontWeight:400}}>(optional)</span></label>
            <select className="setting-input" value={form.referralSource} onChange={e => { setForm({...form, referralSource: e.target.value, referralDetail: ''}); }} style={{cursor:'pointer'}}>
              <option value="">Select an option...</option>
              <option value="Web Search">Web Search</option>
              <option value="AI Tool">AI Tool (ChatGPT, Claude, etc.)</option>
              <option value="Cityworks / Trimble">Cityworks / Trimble</option>
              <option value="LinkedIn">LinkedIn</option>
              <option value="Conference / Trade Show">Conference / Trade Show</option>
              <option value="Existing Customer Referral">Existing Customer Referral</option>
              <option value="Centricity Sales Rep">Centricity Sales Rep</option>
              <option value="Other Referral">Other Referral</option>
              <option value="Cold Outreach">Cold Outreach</option>
              <option value="Other">Other</option>
            </select>
            {(form.referralSource === 'Centricity Sales Rep' || form.referralSource === 'Other Referral') && (
              <input className="setting-input" style={{marginTop:8}} value={form.referralDetail} onChange={e => setForm({...form, referralDetail: e.target.value})} placeholder={form.referralSource === 'Centricity Sales Rep' ? 'Which rep?' : 'Referred by (name or organization)'} />
            )}
          </div>
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)', lineHeight:1.6}}>
            📧 A welcome email with a "Set Your Password" link will be sent to the admin email. The link expires in 48 hours.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creating...' : 'Create City & Send Welcome Email'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Edit City Modal ──────────────────────────────────────────────────────────
function EditCityModal({ city, onClose, onSaved, showToast }) {
  const [form, setForm] = useState({
    cityId: city.id,
    cityName: city.name || '',
    state: city.state || '',
    emailDelivery: city.email_delivery === 1,
    notificationEmails: city.notification_emails || '',
    geofenceEnabled: city.geofence_enabled === 1,
    geofenceMessage: city.geofence_message || '',
    srTypes: JSON.parse(city.sr_types || '[]'),
    planLimit: city.plan_limit || 500,
    auditEmail: city.audit_email || '',
    assignedSalesRep: city.assigned_sales_rep || '',
    isAnswersPlan: city.is_answers_plan === 1,
  });
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      // Update city config
      const res = await apiFetch('/api/config', {
        method: 'POST',
        body: JSON.stringify({
          cityId: form.cityId,
          cityName: form.cityName,
          state: form.state,
          emailDelivery: form.emailDelivery,
          notificationEmails: form.notificationEmails,
          geofenceEnabled: form.geofenceEnabled,
          geofenceMessage: form.geofenceMessage,
          srTypes: form.srTypes,
          auditEmail: form.auditEmail,
          assignedSalesRep: form.assignedSalesRep,
          // preserve existing values
          greeting: city.greeting,
          primaryColor: city.primary_color,
          wkid: city.wkid,
          selectedTemplates: JSON.parse(city.selected_templates || '[]'),
          aliases: JSON.parse(city.aliases || '{}'),
        }),
      });
      const data = await res.json();

      // Update plan limit separately if changed
      if (form.planLimit !== (city.plan_limit || 500)) {
        await apiFetch('/api/admin/set-plan-limit', {
          method: 'POST',
          body: JSON.stringify({ cityId: form.cityId, planLimit: parseInt(form.planLimit) }),
        });
      }

      // Update Answers plan flag if changed
      if (form.isAnswersPlan !== (city.is_answers_plan === 1)) {
        await apiFetch('/api/admin/set-answers-plan', {
          method: 'POST',
          body: JSON.stringify({ cityId: form.cityId, isAnswersPlan: form.isAnswersPlan }),
        });
      }

      if (data.success) onSaved();
      else showToast(data.error || 'Failed to save', 'error');
    } catch { showToast('Connection error', 'error'); }
    setSaving(false);
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit — {city.name}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">City Name</label>
              <input className="setting-input" value={form.cityName} onChange={e => setForm({...form, cityName: e.target.value})} />
            </div>
            <div className="setting-field">
              <label className="setting-label">State</label>
              <input className="setting-input" value={form.state} onChange={e => setForm({...form, state: e.target.value})} maxLength={2} />
            </div>
          </div>

          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:16}}>
            <div className="setting-field">
              <label className="setting-label">Monthly Submission Limit</label>
              <select className="setting-input" value={form.planLimit} onChange={e => setForm({...form, planLimit: parseInt(e.target.value)})} style={{cursor:'pointer'}}>
                <option value={100}>100 — Starter</option>
                <option value={250}>250 — Growth</option>
                <option value={500}>500 — Professional</option>
                <option value={9999}>Unlimited — Enterprise</option>
              </select>
            </div>
            <div className="setting-field">
              <label className="setting-label">Assigned Sales Rep</label></div><input className="setting-input" value={form.assignedSalesRep} onChange={e => setForm({...form, assignedSalesRep: e.target.value})} placeholder="e.g. John Smith" /><div className="setting-field"><label className="setting-label">Audit Email</label>
              <input className="setting-input" value={form.auditEmail} onChange={e => setForm({...form, auditEmail: e.target.value})} placeholder="Same as admin email" />
              <span style={{fontSize:11, color:'var(--text-muted)', marginTop:4}}>Daily audit emails go here. Leave blank to use admin email.</span>
            </div>
          </div>

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>Email Delivery</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>SRs emailed to staff instead of Cityworks</div>
              </div>
              <button className={`toggle-switch ${form.emailDelivery?'on':''}`} onClick={() => setForm(f=>({...f, emailDelivery:!f.emailDelivery}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          {form.emailDelivery && (
            <div className="setting-field">
              <label className="setting-label">Notification Emails</label>
              <input className="setting-input" value={form.notificationEmails} onChange={e => setForm({...form, notificationEmails: e.target.value})} placeholder="dispatch@city.gov, pw@city.gov" />
            </div>
          )}

          {form.emailDelivery && (
            <div className="setting-field">
              <label className="setting-label">SR Types <span style={{color:'var(--text-muted)', fontWeight:400}}>(one per line)</span></label>
              <textarea className="setting-input" rows={5} value={form.srTypes.join('\n')} onChange={e => setForm({...form, srTypes: e.target.value.split('\n').map(s=>s.trim()).filter(Boolean)})} placeholder={"Pothole Report\nStreet Light Outage\nGraffiti"} style={{resize:'vertical', lineHeight:1.6}} />
            </div>
          )}

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)'}}>City Limits Geofencing</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Restrict submissions to city boundary</div>
              </div>
              <button className={`toggle-switch ${form.geofenceEnabled?'on':''}`} onClick={() => setForm(f=>({...f, geofenceEnabled:!f.geofenceEnabled}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          {form.geofenceEnabled && (
            <div className="setting-field">
              <label className="setting-label">Out-of-bounds message</label>
              <input className="setting-input" value={form.geofenceMessage} onChange={e => setForm({...form, geofenceMessage: e.target.value})} placeholder="This address appears to be outside city limits..." />
            </div>
          )}

          {/* CivicBot Answers toggle */}
          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px'}}>
            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
              <div>
                <div style={{fontSize:13, fontWeight:600, color:'var(--text)', display:'flex', alignItems:'center', gap:8}}>
                  🤖 CivicBot Answers
                  {form.isAnswersPlan && <span style={{fontSize:10,fontWeight:700,background:'#8B5CF620',color:'#8B5CF6',border:'1px solid #8B5CF630',borderRadius:4,padding:'2px 7px'}}>ENABLED</span>}
                </div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>Unlocks AI-powered resident Q&A add-on (crawl + embeddings)</div>
              </div>
              <button className={'toggle-switch ' + (form.isAnswersPlan ? 'on' : '')} onClick={() => setForm(f => ({...f, isAnswersPlan: !f.isAnswersPlan}))}><div className="toggle-thumb" /></button>
            </div>
          </div>

          <div style={{background:'var(--surface2)', border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px', fontSize:12, color:'var(--text-muted)'}}>
            <strong style={{color:'var(--text-dim)'}}>City ID:</strong> <span style={{fontFamily:'monospace'}}>{city.id}</span><br/>
            <strong style={{color:'var(--text-dim)'}}>Admin:</strong> {city.admin_email || '—'}<br/>
            <strong style={{color:'var(--text-dim)'}}>Created:</strong> {new Date(city.created_at).toLocaleDateString()}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── City Submissions Modal ───────────────────────────────────────────────────
function CitySubmissionsModal({ city, onClose }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthCount, setMonthCount] = useState(0);

  useEffect(() => {
    apiFetch(`/api/submissions?cityId=${city.id}`)
      .then(r => r.json())
      .then(data => {
        if (data.success) { setSubmissions(data.submissions); setMonthCount(data.monthCount); }
      })
      .finally(() => setLoading(false));
  }, [city.id]);

  function formatDate(dt) {
    if (!dt) return '—';
    const normalized = dt.includes('T') ? dt : dt.replace(' ', 'T') + 'Z';
    return new Date(normalized).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{maxWidth:760}}>
        <div className="modal-header">
          <div>
            <div className="modal-title">📋 {city.name} — Submissions</div>
            <div style={{fontSize:12, color:'var(--text-muted)', marginTop:2}}>{monthCount} this month · {submissions.length} total loaded</div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{padding:'0', maxHeight:'60vh', overflowY:'auto'}}>
          {loading ? (
            <div className="loading"><div className="spinner" />Loading...</div>
          ) : submissions.length === 0 ? (
            <div className="empty">No submissions yet for {city.name}.</div>
          ) : (
            <table className="submissions-table">
              <thead>
                <tr>
                  <th>Confirmation #</th>
                  <th>SR Type</th>
                  <th>Address</th>
                  <th>Contact</th>
                  <th>Via</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(s => (
                  <tr key={s.id}>
                    <td><span style={{fontFamily:'monospace', fontSize:11, color:'var(--accent-light)'}}>{s.confirmation_number}</span></td>
                    <td style={{maxWidth:160, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:12}}>{s.sr_type || '—'}</td>
                    <td style={{maxWidth:180, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis', fontSize:11, color:'var(--text-dim)'}}>{s.geo_address || s.address || '—'}</td>
                    <td style={{fontSize:11, color:'var(--text-dim)'}}>{s.caller_name && s.caller_name !== 'Anonymous' ? s.caller_name : <span style={{color:'var(--text-muted)'}}>Anon</span>}</td>
                    <td><span className={`delivery-badge ${s.delivery_method}`}>{s.delivery_method === 'email' ? '📧' : '🔗'}</span></td>
                    <td style={{fontSize:11, color:'var(--text-muted)', whiteSpace:'nowrap'}}>{formatDate(s.submitted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Answers Page ─────────────────────────────────────────────────────────────
function AnswersPage({ user, showToast }) {
  const cityId = user?.cityId;
  const [status, setStatus] = React.useState(null);
  const [config, setConfig] = React.useState({ startUrl: '', maxPages: 500 });
  const [crawlConfirm, setCrawlConfirm] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [crawling, setCrawling] = React.useState(false);
  const [qaLog, setQaLog] = React.useState([]);
  const [clearConfirm, setClearConfirm] = React.useState(false);
  const [billingStatus, setBillingStatus] = React.useState(null);

  React.useEffect(() => {
    if (!cityId) return;
    Promise.all([loadStatus(), loadConfig(), loadQaLog()]).finally(() => setLoading(false));
  }, [cityId]);

  async function loadStatus() {
    try {
      const res = await apiFetch('/api/admin/crawl/status/' + cityId);
      const data = await res.json();
      setStatus(data);
      if (data.lastRun && data.lastRun.status === 'running') setCrawling(true);
      else setCrawling(false);
    } catch (e) { console.error('[Answers] Status fetch failed', e); }
  }

  async function loadConfig() {
    try {
      const res = await apiFetch('/api/admin/crawl/config/' + cityId);
      const data = await res.json();
      if (data.config) {
        setConfig({
          startUrl: data.config.start_url || '',
          maxPages: data.config.max_pages || 500,
        });
      }
      if (data.billingStatus) setBillingStatus(data.billingStatus);
    } catch (e) { console.error('[Answers] Config fetch failed', e); }
  }

  // Poll every 10s while crawl is running
  React.useEffect(() => {
    if (!crawling) return;
    const interval = setInterval(loadStatus, 10000);
    return () => clearInterval(interval);
  }, [crawling]);

  async function saveConfig() {
    if (!config.startUrl.trim()) { showToast('Start URL is required', 'error'); return; }
    setSaving(true);
    try {
      const res = await apiFetch('/api/admin/crawl/config/' + cityId, {
        method: 'POST',
        body: JSON.stringify({ startUrl: config.startUrl, maxPages: config.maxPages, crawlFrequency: 'weekly' }),
      });
      const data = await res.json();
      if (data.success) showToast('Configuration saved');
      else showToast(data.error || 'Save failed', 'error');
    } catch (e) { showToast('Save failed — check connection', 'error'); }
    setSaving(false);
  }

  async function startCrawl() {
    if (!config.startUrl.trim()) { showToast('Save a Start URL first', 'error'); return; }
    if (!crawlConfirm) { setCrawlConfirm(true); return; }
    setCrawlConfirm(false);
    setCrawling(true);
    try {
      const res = await apiFetch('/api/admin/crawl/start/' + cityId, { method: 'POST' });
      const data = await res.json();
      if (data.success) showToast('Crawl started — status updates every 10 seconds');
      else { showToast(data.error || 'Failed to start crawl', 'error'); setCrawling(false); }
    } catch (e) { showToast('Failed to start crawl', 'error'); setCrawling(false); }
  }

  async function loadQaLog() {
    try {
      const res = await apiFetch('/api/admin/qa-log/' + cityId);
      const data = await res.json();
      if (data.success) setQaLog(data.log);
    } catch (e) { console.error('[Answers] QA log fetch failed', e); }
  }

  async function resolveQaEntry(id, resolved) {
    try {
      await apiFetch('/api/admin/qa-log/' + id, { method: 'PATCH', body: JSON.stringify({ resolved }) });
      setQaLog(prev => prev.map(r => r.id === id ? { ...r, resolved: resolved ? 1 : 0, resolved_at: resolved ? new Date().toISOString().slice(0, 10) : null } : r));
    } catch (e) { showToast('Failed to update entry', 'error'); }
  }

  async function clearQaLog() {
    if (!clearConfirm) { setClearConfirm(true); return; }
    try {
      await apiFetch('/api/admin/qa-log/' + cityId, { method: 'DELETE' });
      setQaLog([]);
      setClearConfirm(false);
      showToast('Q&A log cleared');
    } catch (e) { showToast('Failed to clear log', 'error'); }
  }

  function formatDate(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  if (loading) return <div className="loading"><div className="spinner" />Loading...</div>;

  const lastRun = status?.lastRun;
  const runStatus = lastRun?.status;
  const statusColor = runStatus === 'success' ? 'var(--success)' : runStatus === 'running' ? 'var(--warning)' : runStatus === 'failed' ? 'var(--danger)' : 'var(--text-muted)';
  const statusLabel = runStatus === 'success' ? '✅ Success' : runStatus === 'running' ? '⏳ Running' : runStatus === 'failed' ? '❌ Failed' : '— Never run';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">🤖 CivicBot Answers</div>
          <div className="page-subtitle">Manage the knowledge base that powers resident Q&A for {user?.cityName}</div>
        </div>
        {crawling && (
          <span style={{display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--warning)',background:'#F59E0B15',border:'1px solid #F59E0B30',padding:'8px 14px',borderRadius:8}}>
            <div className="spinner" style={{width:14,height:14,borderWidth:2}} />Crawl in progress...
          </span>
        )}
      </div>

      {/* Status Card */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">📊 Knowledge Base Status</div></div>
        <div className="card-body">
          {status?.demoChunkCount > 0 && (
            <div style={{background:'#F59E0B10',border:'1px solid #F59E0B40',borderRadius:8,padding:'14px 16px',marginBottom:16}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:10}}>
                <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
                <div style={{fontSize:12,color:'#F59E0B'}}>
                  <strong>Trial Demo Data Active</strong> — {status.demoChunkCount} generic municipal sample chunks are loaded so you can test CivicBot Answers right now. All responses are prefixed with "Trial Data — Not real city data." When your trial converts to a paid plan, this sample data is cleared and your actual city website is crawled automatically.
                </div>
              </div>
              <div style={{fontSize:12,color:'var(--text-dim)',marginLeft:26}}>
                <div style={{fontWeight:600,marginBottom:6,color:'var(--text-dim)'}}>Try these sample questions in your widget to see CivicBot Answers in action:</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px 16px'}}>
                  {[
                    'How do I report a pothole?',
                    'What are City Hall hours?',
                    'How do I apply for a building permit?',
                    'How do I set up utility service?',
                    'What parks programs are available?',
                    'How do I report illegal dumping?',
                    'What trash and recycling services are available?',
                  ].map(q => (
                    <span key={q} style={{background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,padding:'3px 8px',fontSize:11,color:'var(--text-dim)',fontStyle:'italic'}}>"{q}"</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:16,marginBottom:16}}>
            {[
              { label: 'Pages Indexed', value: status?.pageCount ?? 0, color: 'var(--accent-light)' },
              { label: 'Real Chunks', value: status?.chunkCount ?? 0, color: '#8B5CF6' },
              { label: 'Last Run Status', value: statusLabel, color: statusColor, small: true },
              { label: 'Last Crawled', value: lastRun ? formatDate(lastRun.started_at) : 'Never', color: 'var(--text)', small: true },
            ].map((card, i) => (
              <div key={i} style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px'}}>
                <div style={{fontSize:11,fontWeight:600,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:0.5,marginBottom:6}}>{card.label}</div>
                <div style={{fontSize:card.small?13:24,fontWeight:700,color:card.color,lineHeight:1.2}}>{card.value}</div>
              </div>
            ))}
          </div>
          {lastRun?.error_message && (
            <div style={{background:'#EF444415',border:'1px solid #EF444430',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--danger)'}}>
              ❌ Last error: {lastRun.error_message}
            </div>
          )}
          {lastRun?.firecrawl_cost_usd > 0 && (
            <div style={{fontSize:12,color:'var(--text-muted)',marginTop:8}}>
              Last crawl cost: ${Number(lastRun.firecrawl_cost_usd).toFixed(4)} · Pages crawled: {lastRun.pages_crawled}
            </div>
          )}
        </div>
      </div>

      {/* Config Card */}
      <div className="card" style={{marginBottom:20}}>
        <div className="card-header"><div className="card-title">⚙️ Crawl Configuration</div></div>
        <div className="card-body">
          <div style={{display:'flex',flexDirection:'column',gap:14}}>
            <div className="setting-field">
              <label className="setting-label">Start URL</label>
              <input
                className="setting-input"
                value={config.startUrl}
                onChange={e => setConfig({...config, startUrl: e.target.value})}
                placeholder="https://www.yourcity.gov"
              />
              <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>CivicBot will crawl this site and all linked pages up to your page limit.</span>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <div className="setting-field">
                <label className="setting-label">Max Pages</label>
                <select className="setting-input" value={config.maxPages} onChange={e => setConfig({...config, maxPages: parseInt(e.target.value)})} style={{cursor:'pointer'}}>
                  <option value={100}>100 pages</option>
                  <option value={300}>300 pages</option>
                  <option value={500}>500 pages</option>
                  <option value={1000}>1,000 pages</option>
                </select>
              </div>
              <div className="setting-field">
                <label className="setting-label">Crawl Schedule</label>
                <div style={{padding:'9px 12px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,fontSize:13,color:'var(--text-dim)'}}>🕑 Weekly — Sundays at 2:00 AM MDT (auto)</div>
                <span style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Crawls run automatically. Use manual crawl below only when needed.</span>
              </div>
            </div>
            <div>
              <button className="save-btn" onClick={saveConfig} disabled={saving} style={{padding:'9px 24px'}}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Actions Card */}
      <div className="card">
        <div className="card-header"><div className="card-title">🚀 Actions</div></div>
        <div className="card-body">
          {(billingStatus === 'trialing' || billingStatus === 'trial') ? (
            <div style={{background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:10,padding:'14px 18px',fontSize:13,color:'var(--text-muted)',lineHeight:1.6}}>
              ⏳ <strong style={{color:'var(--text-dim)'}}>Crawl pending trial activation</strong> — your initial knowledge base crawl will start automatically when your trial converts to a paid plan.
            </div>
          ) : (
            <>
              <div style={{display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
                {!crawlConfirm ? (
                  <button
                    onClick={startCrawl}
                    disabled={crawling || !config.startUrl.trim()}
                    style={{
                      padding:'10px 24px',
                      background: crawling || !config.startUrl.trim() ? 'var(--surface3)' : 'var(--accent)',
                      border:'none',borderRadius:8,color:'white',fontSize:13,fontWeight:600,fontFamily:'inherit',
                      cursor: crawling || !config.startUrl.trim() ? 'not-allowed' : 'pointer',
                      display:'flex',alignItems:'center',gap:8,transition:'background 0.2s'
                    }}
                  >
                    {crawling
                      ? <><div className="spinner" style={{width:14,height:14,borderColor:'#ffffff40',borderTopColor:'#fff',borderWidth:2}} />Crawling...</>
                      : '🕷️ Run Manual Crawl'}
                  </button>
                ) : (
                  <div style={{display:'flex',alignItems:'center',gap:10,background:'#F59E0B15',border:'1px solid #F59E0B40',borderRadius:10,padding:'10px 16px'}}>
                    <span style={{fontSize:13,color:'var(--warning)',fontWeight:600}}>⚠️ This uses Firecrawl credits. Confirm?</span>
                    <button onClick={startCrawl} style={{padding:'6px 16px',background:'#F59E0B',border:'none',borderRadius:6,color:'white',fontSize:12,fontWeight:600,fontFamily:'inherit',cursor:'pointer'}}>Yes, Run Crawl</button>
                    <button onClick={() => setCrawlConfirm(false)} style={{padding:'6px 14px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Cancel</button>
                  </div>
                )}
                {crawling && <span style={{fontSize:12,color:'var(--text-muted)'}}>Usually takes 2–10 minutes depending on site size. Status refreshes automatically.</span>}
              </div>
              {!config.startUrl.trim() && (
                <div style={{marginTop:12,fontSize:12,color:'var(--warning)'}}>⚠️ Save a Start URL above before running a crawl.</div>
              )}
            </>
          )}
          <div style={{marginTop:16,background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:8,padding:'10px 14px',fontSize:12,color:'var(--text-muted)',lineHeight:1.6}}>
            💡 <strong style={{color:'var(--text-dim)'}}>How it works:</strong> CivicBot crawls your city website, splits content into searchable chunks, and generates AI embeddings so residents can ask natural language questions and get grounded answers with source links.
          </div>
        </div>
      </div>

      {/* Unanswered Q&A Log */}
      <div className="card" style={{marginTop:20}}>
        <div className="card-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div>
            <div className="card-title">❓ Unanswered Q&A Log</div>
            <span style={{fontSize:12,color:'var(--text-muted)'}}>Questions residents asked that CivicBot couldn't answer</span>
          </div>
          {qaLog.length > 0 && (
            <div style={{display:'flex',gap:8,alignItems:'center'}}>
              {clearConfirm ? (
                <>
                  <span style={{fontSize:12,color:'var(--warning)',fontWeight:600}}>Are you sure? This cannot be undone.</span>
                  <button onClick={clearQaLog} style={{padding:'5px 14px',background:'var(--danger)',border:'none',borderRadius:6,color:'white',fontSize:12,fontWeight:600,fontFamily:'inherit',cursor:'pointer'}}>Yes, Clear All</button>
                  <button onClick={() => setClearConfirm(false)} style={{padding:'5px 12px',background:'var(--surface3)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Cancel</button>
                </>
              ) : (
                <button onClick={() => setClearConfirm(true)} style={{padding:'5px 14px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:6,color:'var(--text-muted)',fontSize:12,fontFamily:'inherit',cursor:'pointer'}}>Clear All</button>
              )}
            </div>
          )}
        </div>
        <div className="card-body" style={{padding:0}}>
          {qaLog.length === 0 ? (
            <div style={{padding:'24px',textAlign:'center',color:'var(--text-muted)',fontSize:13}}>No unanswered questions yet — great job! 🎉</div>
          ) : (
            <div style={{overflowY:'auto',maxHeight:400}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                <thead>
                  <tr style={{borderBottom:'1px solid var(--border)',background:'var(--surface2)'}}>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:50}}>#</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5}}>Question</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:90}}>Asked</th>
                    <th style={{padding:'8px 14px',textAlign:'center',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:80}}>Resolved</th>
                    <th style={{padding:'8px 14px',textAlign:'left',fontWeight:600,color:'var(--text-muted)',fontSize:11,textTransform:'uppercase',letterSpacing:0.5,width:90}}>Resolved On</th>
                  </tr>
                </thead>
                <tbody>
                  {qaLog.map(row => (
                    <tr key={row.id} style={{borderBottom:'1px solid var(--border)',opacity: row.resolved ? 0.55 : 1, background: row.resolved ? 'var(--surface2)' : 'transparent'}}>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',fontVariantNumeric:'tabular-nums'}}>{row.id}</td>
                      <td style={{padding:'10px 14px',color:'var(--text)',lineHeight:1.4,textDecoration: row.resolved ? 'line-through' : 'none'}}>{row.question}</td>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{row.created_at ? row.created_at.slice(0,10) : '—'}</td>
                      <td style={{padding:'10px 14px',textAlign:'center'}}>
                        <input type="checkbox" checked={!!row.resolved} onChange={e => resolveQaEntry(row.id, e.target.checked)} style={{width:16,height:16,cursor:'pointer',accentColor:'var(--accent)'}} />
                      </td>
                      <td style={{padding:'10px 14px',color:'var(--text-muted)',whiteSpace:'nowrap'}}>{row.resolved_at || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Embed Code Generator ──────────────────────────────────────────────────────
function generateEmbedCode(templates, ids) {
  const templateIds = ids || templates.map(t => t.ProblemSid);
  return `<script src="https://civicbot.centricitygis.com/widget.js"\n  data-city-id="your-city-id"\n  data-templates="${templateIds.join(',')}"\n  data-color="#3B82F6"\n  async>\n</script>`;
}
